import { useState, useEffect, Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import IotDevicesTab from "@/components/IotDevicesTab";
import {
  Users, Settings, Plus, KeyRound, Wrench, ClipboardList, Activity, Sheet,
  CheckCircle2, XCircle, ChevronDown, Trash2, Pencil, RefreshCw, ExternalLink, Download,
  Eye, EyeOff, Copy, Wand2, Mail, Send, FileText, Upload, FileSpreadsheet, GitBranch,
  AlertTriangle, Box, Printer, QrCode, Cpu, ShieldCheck, Save, Target, Palette, X,
} from "lucide-react";

const ROLES = ["Operator", "Inspector", "Stores", "Supervisor", "Main Admin"];
const MACHINE_STATUSES = ["Active", "Maintenance", "Idle"];
const CHECK_TYPES = ["Measured", "Attribute", "Visual"];

const AdminPanel = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(user?.role === "Main Admin" ? "users" : "shortfalls");

  // Honour ?tab=xxx and ?fg=<batch_no> from the URL — used by the Dashboard
  // shortfall alert and by QR code deep-links printed on FG batch labels.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("tab");
    if (t) setTab(t);
    const fg = params.get("fg");
    if (fg) {
      setTab("trace");
      setTraceJobId(fg);
      // Defer the lookup until the trace handler is bound
      setTimeout(() => lookupTrace(fg), 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const [users, setUsers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [tools, setTools] = useState([]);
  const [parts, setParts] = useState([]);
  const [inspPlan, setInspPlan] = useState([]);
  const [inspToolFilter, setInspToolFilter] = useState("");
  const [auditLogs, setAuditLogs] = useState([]);
  const [sheetsStatus, setSheetsStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [reportsStatus, setReportsStatus] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);
  const [hourlyStatus, setHourlyStatus] = useState(null);
  const [hourlyPreview, setHourlyPreview] = useState(null);
  const [sendingHourly, setSendingHourly] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [importing, setImporting] = useState(false);
  const [traceJobId, setTraceJobId] = useState("");
  const [traceData, setTraceData] = useState(null);
  const [fgTraceData, setFgTraceData] = useState(null);
  const [traceParts, setTraceParts] = useState([]);
  const [traceSelectedPart, setTraceSelectedPart] = useState(null); // {part_no, part_name}
  const [tracePartJobs, setTracePartJobs] = useState(null); // [] or null while loading
  const [rmMapping, setRmMapping] = useState(null); // {total, jobs, ...}
  const [showRmMapping, setShowRmMapping] = useState(false);
  const [rmMappingLoading, setRmMappingLoading] = useState(false);
  const [rmAutoMatching, setRmAutoMatching] = useState(false);
  const [syncingHistorical, setSyncingHistorical] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [fgBatches, setFgBatches] = useState([]);
  const [tracing, setTracing] = useState(false);
  const [shortfalls, setShortfalls] = useState([]);
  const [shortfallDateFilter, setShortfallDateFilter] = useState("");
  const [resolvingShortfall, setResolvingShortfall] = useState(null); // job object
  const [resolveAction, setResolveAction] = useState("adjust");
  const [resolveNote, setResolveNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // User modal state
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showPwdText, setShowPwdText] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(null);  // {user_id, password} after reset
  const [userForm, setUserForm] = useState({ user_id: "", full_name: "", password: "", role: "Operator", department: "", machine_assigned: "" });
  // Dynamic role list — built-ins plus any active custom roles created in
  // the Role Access tab. Refreshed whenever WS says role list changed.
  const [dynamicRoles, setDynamicRoles] = useState(ROLES);
  const [customRoles, setCustomRoles] = useState([]);
  const { rolesChangedAt } = useAuth();
  const refreshRoles = async () => {
    try {
      const res = await api.get("/admin/roles");
      const all = res.data?.all || ROLES;
      setDynamicRoles(all);
      const custom = await api.get("/admin/custom-roles");
      setCustomRoles(custom.data || []);
    } catch { /* non-admins hit 403 — ignore */ }
  };
  useEffect(() => {
    if (user?.role === "Main Admin") refreshRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, rolesChangedAt]);

  // Machine modal state
  const [showMachineModal, setShowMachineModal] = useState(false);
  const [machineForm, setMachineForm] = useState({ machine_code: "", machine_type: "Power Press", tonnage: "", status: "Active", notes: "" });

  // Tool modal state
  const [showToolModal, setShowToolModal] = useState(false);
  const [toolEditing, setToolEditing] = useState(null);
  const [toolForm, setToolForm] = useState(emptyToolForm());

  // Part modal state
  const [showPartModal, setShowPartModal] = useState(false);
  const [partEditing, setPartEditing] = useState(null);
  const [partForm, setPartForm] = useState(emptyPartForm());

  // Inspection row modal state
  const [showInspModal, setShowInspModal] = useState(false);
  const [inspEditing, setInspEditing] = useState(null);
  const [inspForm, setInspForm] = useState(emptyInspForm());

  function emptyToolForm() {
    return {
      tool_no: "", tool_name: "", part_name: "", part_no: "", customer: "",
      operation: "", rm_code: "", rm_grade: "", thickness_mm: "", width_mm: "",
      no_of_cavities: "1", min_tonnage_t: "", strokes_per_min: "", target_per_day: "",
      operation_seq: "", total_operations: "", gross_weight_g: "",
    };
  }
  function emptyPartForm() {
    return {
      internal_part_no: "", part_no: "", part_name: "", customer: "",
      customer_drawing_no: "", rm_grade: "", rm_thickness: "", rm_width: "",
    };
  }
  function emptyInspForm() {
    return {
      tool_no: "", part_no: "", s_no: "", dimension_description: "",
      nominal: "", tolerance_plus: "", tolerance_minus: "", lsl: "", usl: "",
      instrument: "", check_type: "Measured", multi_point_count: "1",
      cavities: "1", first_sample_size: "2", periodic_sample_size: "1",
      periodic_interval_hours: "2", inspector_note: ""
    };
  }

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchCommon = async () => {
    setLoading(true);
    try {
      const [usersRes, machinesRes, toolsRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/machines"),
        api.get("/admin/tools"),
      ]);
      setUsers(usersRes.data);
      setMachines(machinesRes.data);
      setTools(toolsRes.data);
    } catch {}
    setLoading(false);
  };

  const fetchInspPlan = async (tool_no = "") => {
    try {
      const url = tool_no ? `/admin/inspection-plan?tool_no=${encodeURIComponent(tool_no)}` : "/admin/inspection-plan";
      const res = await api.get(url);
      setInspPlan(res.data);
    } catch {}
  };

  const fetchAuditLog = async () => {
    try {
      const res = await api.get("/audit-log");
      setAuditLogs(res.data);
    } catch {}
  };

  const fetchSheetsStatus = async () => {
    try {
      const res = await api.get("/sheets/status");
      setSheetsStatus(res.data);
    } catch {}
  };

  const connectSheets = () => {
    const token = localStorage.getItem("ampl_token");
    if (!token) { showToast("Please log in again", "error"); return; }
    const base = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${base}/api/oauth/sheets/login?token=${encodeURIComponent(token)}`;
  };

  const syncSheets = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/sheets/sync");
      showToast(`Synced ${Object.keys(res.data.counts || {}).length} tabs`);
      fetchSheetsStatus();
    } catch (err) {
      showToast(err.response?.data?.detail || "Sync failed", "error");
    }
    setSyncing(false);
  };

  const disconnectSheets = async () => {
    if (!window.confirm("Disconnect Google Sheets? You'll need to reconnect to sync again.")) return;
    try {
      await api.post("/sheets/disconnect");
      showToast("Google Sheets disconnected");
      fetchSheetsStatus();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed", "error");
    }
  };

  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [previewingPdf, setPreviewingPdf] = useState(false);

  const downloadExcel = async () => {
    setDownloadingExcel(true);
    showToast("Building Excel — this can take up to 3 minutes for large plants...", "info");
    try {
      const res = await api.get("/admin/export/excel", {
        responseType: "blob",
        timeout: 300000,  // 5 min — 11-tab workbook of ~10k records is slow
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const dispo = res.headers["content-disposition"] || "";
      const match = dispo.match(/filename="(.+?)"/);
      a.download = match ? match[1] : `AMPL_PlantData_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast("Excel downloaded successfully");
    } catch (err) {
      const msg = err.code === "ECONNABORTED"
        ? "Excel build timed out (5 min). Try again — server may need a moment."
        : err.response?.data?.detail || err.message || "Download failed";
      showToast(msg, "error");
    }
    setDownloadingExcel(false);
  };

  useEffect(() => { fetchCommon(); fetchSheetsStatus(); }, []);
  useEffect(() => {
    if (tab === "inspection") fetchInspPlan(inspToolFilter);
    if (tab === "audit") fetchAuditLog();
    if (tab === "sheets") fetchSheetsStatus();
    if (tab === "reports") fetchReportsStatus();
    if (tab === "parts") fetchParts();
    if (tab === "shortfalls") fetchShortfalls();
    if (tab === "fg") fetchFgBatches();
    if (tab === "trace") {
      fetchTraceParts();
      fetchRmMapping();
    }
  }, [tab, inspToolFilter, shortfallDateFilter]);

  const fetchParts = async () => {
    try {
      const res = await api.get("/admin/parts");
      setParts(res.data);
    } catch {}
  };

  const fetchReportsStatus = async () => {
    try {
      const res = await api.get("/admin/reports/status");
      setReportsStatus(res.data);
    } catch {}
    try {
      const [s, p] = await Promise.all([
        api.get("/admin/hourly-inspection/status"),
        api.get("/admin/hourly-inspection/preview"),
      ]);
      setHourlyStatus(s.data);
      setHourlyPreview(p.data);
    } catch {}
  };

  const sendHourlyNow = async () => {
    if (!window.confirm("Send the hourly inspection-frequency email now to engineering@ + quality@?")) return;
    setSendingHourly(true);
    try {
      const res = await api.post("/admin/hourly-inspection/send-now");
      if (res.data?.skipped === "no_running_jobs") {
        showToast("No running jobs — email skipped.", "info");
      } else if (res.data?.success) {
        showToast("Hourly inspection email sent.");
      } else {
        showToast("Send attempted but Resend returned an error (check domain verification).", "error");
      }
      fetchReportsStatus();
    } catch (err) {
      showToast(err.response?.data?.detail || "Send failed", "error");
    }
    setSendingHourly(false);
  };

  const sendReportNow = async () => {
    if (!window.confirm("Build today's PDF + Excel and email it now to all configured recipients?")) return;
    setSendingReport(true);
    showToast("Building & sending email — up to 3 minutes on large plants...", "info");
    try {
      const res = await api.post("/admin/reports/send-now", null, { timeout: 300000 });
      if (res.data?.success) {
        showToast(`Report emailed (${res.data.recipients?.length || 0} recipient${(res.data.recipients?.length || 0) > 1 ? "s" : ""})`);
      } else {
        showToast(`Send returned failure: ${res.data?.error || "unknown"}`, "error");
      }
      fetchReportsStatus();
    } catch (err) {
      const msg = err.code === "ECONNABORTED"
        ? "Email send timed out (5 min). Recent Sends will show final status once it finishes on the server."
        : err.response?.data?.detail || err.message || "Send failed";
      showToast(msg, "error");
    }
    setSendingReport(false);
  };

  const previewPdf = async () => {
    setPreviewingPdf(true);
    showToast("Building preview PDF...", "info");
    try {
      const res = await api.get("/admin/reports/preview-pdf", {
        responseType: "blob",
        timeout: 300000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const opened = window.open(url, "_blank");
      if (!opened) {
        // Popup blocker — fall back to same-tab download
        const a = document.createElement("a");
        a.href = url;
        a.download = `AMPL_ShiftEnd_Preview_${new Date().toISOString().slice(0,10)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        showToast("PDF downloaded (browser blocked new-tab popup)", "info");
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      const msg = err.code === "ECONNABORTED"
        ? "PDF build timed out (5 min). Try again."
        : err.response?.data?.detail || err.message || "Preview failed";
      showToast(msg, "error");
    }
    setPreviewingPdf(false);
  };

  const uploadHistorical = async (kind, file) => {
    if (!file) return;
    const isWipe = kind === "tools" || kind === "rm" || kind === "production" || kind === "inspection_plan";
    const wipeTargets = {
      tools: "all existing tools",
      rm: "all existing RM receipts AND issues",
      production: "all Stage-1 and Stage-2 production records",
      inspection_plan: "all existing inspection plan rows",
    };
    const msg = isWipe
      ? `⚠ This will WIPE ${wipeTargets[kind]} and re-import from ${file.name}. Continue?`
      : `Import ${kind} data from ${file.name}? Existing records with the same key will be UPDATED.`;
    if (!window.confirm(msg)) return;
    setImporting(true);
    setImportStatus(null);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const res = await api.post("/admin/historical-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setImportStatus(res.data);
      showToast(`Imported ${kind}: ${Object.entries(res.data.added).map(([k, v]) => `${k} ${v >= 0 ? "+" : ""}${v}`).join(", ")}`);
    } catch (err) {
      showToast(err.response?.data?.detail || "Import failed", "error");
    }
    setImporting(false);
  };

  const downloadImportTemplate = async (kind) => {
    try {
      const res = await api.get(`/admin/import-template/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const dispo = res.headers["content-disposition"] || "";
      const m = dispo.match(/filename="(.+?)"/);
      a.download = m ? m[1] : `${kind}_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast("Template downloaded");
    } catch (err) {
      showToast(err.response?.data?.detail || "Download failed", "error");
    }
  };

  // Show toast on return from OAuth
  useEffect(() => {
    const url = new URL(window.location.href);
    const sheets = url.searchParams.get("sheets");
    if (sheets === "connected") { showToast("Google Sheets connected!"); fetchSheetsStatus(); }
    else if (sheets === "error") { showToast(`Connect failed: ${url.searchParams.get("msg") || ""}`, "error"); }
    else if (sheets === "no_refresh_token") { showToast("Re-connect — Google didn't return refresh token", "error"); }
    if (sheets) {
      url.searchParams.delete("sheets");
      url.searchParams.delete("msg");
      window.history.replaceState({}, "", url.toString());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============== USER ACTIONS ==============
  const toggleActive = async (uid, current) => {
    try {
      await api.put(`/admin/users/${uid}`, { active: current === "Y" ? "N" : "Y" });
      setUsers((prev) => prev.map((u) => u.user_id === uid ? { ...u, active: current === "Y" ? "N" : "Y" } : u));
      showToast(`User ${uid} ${current === "Y" ? "deactivated" : "activated"}`);
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed", "error");
    }
  };

  const changeRole = async (targetUser, newRole) => {
    if (newRole === targetUser.role) return;
    const ok = window.confirm(
      `Change role of ${targetUser.full_name} (${targetUser.user_id}) from "${targetUser.role}" to "${newRole}"?\n\n` +
      `They will need to log out and log back in for the new permissions to apply.`
    );
    if (!ok) return;
    try {
      await api.put(`/admin/users/${targetUser.user_id}`, { role: newRole });
      setUsers((prev) => prev.map((u) => u.user_id === targetUser.user_id ? { ...u, role: newRole } : u));
      showToast(`Role updated: ${targetUser.user_id} → ${newRole}`);
    } catch (err) {
      showToast(err.response?.data?.detail || "Role change failed", "error");
    }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 4) { showToast("Min 4 characters required", "error"); return; }
    try {
      await api.post(`/admin/users/${selectedUser.user_id}/reset-password`, { new_password: newPassword });
      setResetSuccess({ user_id: selectedUser.user_id, name: selectedUser.full_name, password: newPassword });
      showToast(`Password reset for ${selectedUser.user_id}`);
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed", "error");
    }
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let p = "";
    for (let i = 0; i < 8; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setNewPassword(p);
    setShowPwdText(true);
  };

  const closePwdModal = () => {
    setShowPwdModal(false);
    setNewPassword("");
    setShowPwdText(false);
    setResetSuccess(null);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast("Copied to clipboard");
    } catch {
      showToast("Copy failed — please select and copy manually", "error");
    }
  };

  const deleteUser = async (uid, name) => {
    if (!window.confirm(`Permanently delete user ${uid} (${name})? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      showToast(`User ${uid} deleted`);
      fetchCommon();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed", "error");
    }
  };

  const createUser = async () => {
    if (!userForm.user_id || !userForm.full_name || !userForm.password) {
      showToast("User ID, Name and Password are required", "error"); return;
    }
    try {
      await api.post("/admin/users", { ...userForm, machine_assigned: userForm.machine_assigned || null });
      showToast(`User ${userForm.user_id.toUpperCase()} created`);
      setShowUserModal(false);
      setUserForm({ user_id: "", full_name: "", password: "", role: "Operator", department: "", machine_assigned: "" });
      fetchCommon();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to create user", "error");
    }
  };

  // ============== MACHINE ACTIONS ==============
  const updateMachineStatus = async (code, status) => {
    try {
      await api.put(`/admin/machines/${code}`, { status });
      setMachines((prev) => prev.map((m) => m.machine_code === code ? { ...m, status } : m));
      showToast(`${code} status updated to ${status}`);
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed", "error");
    }
  };

  const deleteMachine = async (code, hard = false) => {
    const msg = hard
      ? `HARD-DELETE ${code}? This removes the record entirely.`
      : `Retire ${code}? It will be hidden from selection but its history remains.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/machines/${code}${hard ? "?hard=true" : ""}`);
      if (hard) {
        setMachines((prev) => prev.filter((m) => m.machine_code !== code));
      } else {
        setMachines((prev) => prev.map((m) => m.machine_code === code ? { ...m, status: "Retired", active: "N" } : m));
      }
      showToast(`${code} ${hard ? "deleted" : "retired"}`);
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed", "error");
    }
  };

  const createMachine = async () => {
    if (!machineForm.machine_code || !machineForm.tonnage) {
      showToast("Machine code and tonnage are required", "error"); return;
    }
    try {
      await api.post("/admin/machines", { ...machineForm, tonnage: parseInt(machineForm.tonnage) });
      showToast(`Machine ${machineForm.machine_code} added`);
      setShowMachineModal(false);
      setMachineForm({ machine_code: "", machine_type: "Power Press", tonnage: "", status: "Active", notes: "" });
      fetchCommon();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to add machine", "error");
    }
  };

  // ============== TOOL ACTIONS ==============
  const openCreateTool = () => {
    setToolEditing(null);
    setToolForm(emptyToolForm());
    setShowToolModal(true);
  };
  const openEditTool = (t) => {
    setToolEditing(t.tool_no);
    setToolForm({
      tool_no: t.tool_no || "", tool_name: t.tool_name || "", part_name: t.part_name || "",
      part_no: t.part_no || "", customer: t.customer || "", operation: t.operation || "",
      rm_code: t.rm_code || "", rm_grade: t.rm_grade || "",
      thickness_mm: t.thickness_mm ?? "", width_mm: t.width_mm ?? "",
      no_of_cavities: String(t.no_of_cavities ?? 1), min_tonnage_t: String(t.min_tonnage_t ?? ""),
      strokes_per_min: String(t.strokes_per_min ?? ""), target_per_day: String(t.target_per_day ?? ""),
      operation_seq: String(t.operation_seq ?? ""),
      total_operations: String(t.total_operations ?? ""),
      gross_weight_g: String(t.gross_weight_g ?? ""),
    });
    setShowToolModal(true);
  };

  const saveTool = async () => {
    if (!toolForm.tool_no || !toolForm.part_name || !toolForm.part_no || !toolForm.operation) {
      showToast("Tool No, Part Name, Part No and Operation are required", "error"); return;
    }
    const payload = {
      ...toolForm,
      thickness_mm: parseFloat(toolForm.thickness_mm) || 0,
      width_mm: parseFloat(toolForm.width_mm) || 0,
      no_of_cavities: parseInt(toolForm.no_of_cavities) || 1,
      min_tonnage_t: parseInt(toolForm.min_tonnage_t) || 0,
      strokes_per_min: parseInt(toolForm.strokes_per_min) || 0,
      target_per_day: parseInt(toolForm.target_per_day) || 0,
      operation_seq: parseInt(toolForm.operation_seq) || null,
      total_operations: parseInt(toolForm.total_operations) || null,
      gross_weight_g: parseFloat(toolForm.gross_weight_g) || 0,
    };
    try {
      if (toolEditing) {
        const { tool_no, ...updateBody } = payload;
        await api.put(`/admin/tools/${toolEditing}`, updateBody);
        showToast(`Tool ${toolEditing} updated`);
      } else {
        await api.post("/admin/tools", payload);
        showToast(`Tool ${payload.tool_no} created`);
      }
      setShowToolModal(false);
      fetchCommon();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed", "error");
    }
  };

  // Inline patch helper — used by the inline gross-weight editor
  const patchTool = async (tool_no, patch) => {
    try {
      await api.put(`/admin/tools/${tool_no}`, patch);
      // Optimistic local update so the row re-renders without a full reload
      setTools((prev) => prev.map((t) => t.tool_no === tool_no ? { ...t, ...patch } : t));
      showToast(`Tool ${tool_no} updated`);
    } catch (err) {
      showToast(err.response?.data?.detail || "Update failed", "error");
      fetchCommon();  // reload to drop optimistic state
    }
  };

  const deleteTool = async (tool_no) => {
    if (!window.confirm(`Deactivate tool ${tool_no}? It will be hidden from production but data preserved.`)) return;
    try {
      await api.delete(`/admin/tools/${tool_no}`);
      showToast(`Tool ${tool_no} deactivated`);
      fetchCommon();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed", "error");
    }
  };

  // ============== PART ACTIONS ==============
  const openCreatePart = () => {
    setPartEditing(null);
    setPartForm(emptyPartForm());
    setShowPartModal(true);
  };
  const openEditPart = (p) => {
    setPartEditing(p.internal_part_no);
    setPartForm({
      internal_part_no: p.internal_part_no || "",
      part_no: p.part_no || "",
      part_name: p.part_name || "",
      customer: p.customer || "",
      customer_drawing_no: p.customer_drawing_no || "",
      rm_grade: p.rm_grade || "",
      rm_thickness: p.rm_thickness ?? "",
      rm_width: p.rm_width || "",
    });
    setShowPartModal(true);
  };
  const savePart = async () => {
    if (!partForm.internal_part_no || !partForm.part_name || !partForm.part_no) {
      showToast("Internal Part No, Part No and Part Name are required", "error");
      return;
    }
    const payload = {
      ...partForm,
      rm_thickness: parseFloat(partForm.rm_thickness) || 0,
    };
    try {
      if (partEditing) {
        const { internal_part_no, ...body } = payload;
        await api.put(`/admin/parts/${partEditing}`, body);
        showToast(`Part ${partEditing} updated`);
      } else {
        await api.post("/admin/parts", payload);
        showToast(`Part ${payload.internal_part_no} created`);
      }
      setShowPartModal(false);
      fetchParts();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed", "error");
    }
  };
  const deletePart = async (ipn) => {
    if (!window.confirm(`Deactivate part ${ipn}? It will be hidden from selection but data preserved.`)) return;
    try {
      await api.delete(`/admin/parts/${ipn}`);
      showToast(`Part ${ipn} deactivated`);
      fetchParts();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed", "error");
    }
  };

  // ============== INSPECTION PLAN ACTIONS ==============
  const openCreateInsp = () => {
    setInspEditing(null);
    setInspForm({ ...emptyInspForm(), tool_no: inspToolFilter });
    setShowInspModal(true);
  };

  const openEditInsp = (row) => {
    setInspEditing({ tool_no: row.tool_no, s_no: row.s_no });
    setInspForm({
      tool_no: row.tool_no, part_no: row.part_no || "", s_no: String(row.s_no),
      dimension_description: row.dimension_description || "",
      nominal: row.nominal ?? "", tolerance_plus: row.tolerance_plus ?? "",
      tolerance_minus: row.tolerance_minus ?? "", lsl: row.lsl ?? "", usl: row.usl ?? "",
      instrument: row.instrument || "", check_type: row.check_type || "Measured",
      multi_point_count: String(row.multi_point_count ?? 1),
      cavities: String(row.cavities ?? 1),
      first_sample_size: String(row.first_sample_size ?? 2),
      periodic_sample_size: String(row.periodic_sample_size ?? 1),
      periodic_interval_hours: String(row.periodic_interval_hours ?? 2),
      inspector_note: row.inspector_note || ""
    });
    setShowInspModal(true);
  };

  const saveInsp = async () => {
    if (!inspForm.tool_no || !inspForm.s_no || !inspForm.dimension_description) {
      showToast("Tool, S.No and Dimension are required", "error"); return;
    }
    const numOrNull = (v) => v === "" || v === null ? null : parseFloat(v);
    const payload = {
      ...inspForm,
      s_no: parseInt(inspForm.s_no),
      nominal: numOrNull(inspForm.nominal),
      tolerance_plus: numOrNull(inspForm.tolerance_plus),
      tolerance_minus: numOrNull(inspForm.tolerance_minus),
      lsl: numOrNull(inspForm.lsl),
      usl: numOrNull(inspForm.usl),
      multi_point_count: parseInt(inspForm.multi_point_count) || 1,
      cavities: parseInt(inspForm.cavities) || 1,
      first_sample_size: parseInt(inspForm.first_sample_size) || 2,
      periodic_sample_size: parseInt(inspForm.periodic_sample_size) || 1,
      periodic_interval_hours: parseInt(inspForm.periodic_interval_hours) || 2,
    };
    try {
      if (inspEditing) {
        const { tool_no, part_no, ...updateBody } = payload;
        await api.put(`/admin/inspection-plan/${inspEditing.tool_no}/${inspEditing.s_no}`, updateBody);
        showToast(`Inspection row updated`);
      } else {
        await api.post("/admin/inspection-plan", payload);
        showToast(`Inspection row created`);
      }
      setShowInspModal(false);
      fetchInspPlan(inspToolFilter);
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed", "error");
    }
  };

  const deleteInsp = async (tool_no, s_no) => {
    if (!window.confirm(`Delete inspection row ${tool_no} #${s_no}?`)) return;
    try {
      await api.delete(`/admin/inspection-plan/${tool_no}/${s_no}`);
      showToast(`Inspection row deleted`);
      fetchInspPlan(inspToolFilter);
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed", "error");
    }
  };

  // ============== RENDER ==============
  const isAdmin = user?.role === "Main Admin";
  const allTabs = [
    { id: "users", label: "Users", icon: Users, count: users.length, adminOnly: true },
    { id: "machines", label: "Machines", icon: Settings, count: machines.length, adminOnly: true },
    { id: "tools", label: "Tools", icon: Wrench, count: tools.filter(t => t.active === "Y").length, adminOnly: true },
    { id: "parts", label: "Parts", icon: Box, count: parts.filter(p => p.active !== "N").length || null, adminOnly: true },
    { id: "inspection", label: "Inspection Plan", icon: ClipboardList, count: null, adminOnly: true },
    { id: "audit", label: "Audit Log", icon: Activity, count: null },
    { id: "sheets", label: "Data Export", icon: Sheet, count: null },
    { id: "reports", label: "Daily Reports", icon: Mail, count: null },
    { id: "import", label: "Historical Import", icon: Upload, count: null, adminOnly: true },
    { id: "trace", label: "Job Trace", icon: GitBranch, count: null },
    { id: "fg", label: "FG Batches", icon: Box, count: null },
    { id: "shortfalls", label: "WIP Shortfalls", icon: AlertTriangle, count: null },
    { id: "iot", label: "IoT Devices", icon: Cpu, count: null, adminOnly: true },
    { id: "bulk-tools", label: "Bulk Tools", icon: Upload, count: null, adminOnly: true },
    { id: "reset", label: "Reset Test Data", icon: Trash2, count: null, adminOnly: true },
    { id: "roles", label: "Role Access", icon: ShieldCheck, count: null, adminOnly: true },
    { id: "root-causes", label: "Root Causes", icon: Target, count: null, adminOnly: true },
  ];
  const tabs = allTabs.filter(t => isAdmin || !t.adminOnly);

  const fetchShortfalls = async () => {
    try {
      const url = shortfallDateFilter
        ? `/wip-shortfalls?date=${shortfallDateFilter}`
        : "/wip-shortfalls";
      const res = await api.get(url);
      setShortfalls(res.data);
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to load shortfalls", "error");
    }
  };

  const submitResolve = async () => {
    if (!resolvingShortfall) return;
    try {
      await api.post(`/wip-shortfalls/${resolvingShortfall.job_id}/resolve`, {
        action: resolveAction,
        note: resolveNote,
      });
      showToast(`Shortfall ${resolvingShortfall.job_id} ${resolveAction === "adjust" ? "adjusted" : "dismissed"}`);
      setResolvingShortfall(null);
      setResolveNote("");
      fetchShortfalls();
    } catch (err) {
      showToast(err.response?.data?.detail || "Resolve failed", "error");
    }
  };

  const lookupTrace = async (jid) => {
    if (!jid) return;
    setTracing(true);
    setTraceData(null);
    setFgTraceData(null);
    try {
      const trimmed = jid.trim();
      // FG batch / invoice lookup if input looks like an FG batch number or invoice (contains "-L" or starts with "INV")
      const looksLikeFgOrInvoice = /-L\d+-/.test(trimmed) || /^INV/i.test(trimmed);
      if (looksLikeFgOrInvoice) {
        const isInvoice = /^INV/i.test(trimmed);
        const param = isInvoice ? `invoice_no=${encodeURIComponent(trimmed)}` : `fg_batch_no=${encodeURIComponent(trimmed)}`;
        const res = await api.get(`/trace/lookup?${param}`);
        if (!res.data.results || res.data.results.length === 0) {
          showToast(isInvoice ? `No FG batches found for invoice ${trimmed}` : `FG batch ${trimmed} not found`, "error");
        } else {
          setFgTraceData(res.data);
        }
      } else {
        const res = await api.get(`/trace/${encodeURIComponent(trimmed)}`);
        setTraceData(res.data);
      }
    } catch (err) {
      showToast(err.response?.data?.detail || "Trace lookup failed", "error");
    }
    setTracing(false);
  };

  const fetchTraceParts = async () => {
    try {
      const res = await api.get("/trace/parts");
      setTraceParts(res.data.parts || []);
    } catch {}
  };

  const selectTracePart = async (p) => {
    setTraceSelectedPart(p);
    setTracePartJobs(null);
    // Reset right-side job/FG details so the new part list is the focus
    setTraceData(null);
    setFgTraceData(null);
    try {
      const q = p.part_no
        ? `part_no=${encodeURIComponent(p.part_no)}`
        : `part_name=${encodeURIComponent(p.part_name)}`;
      const res = await api.get(`/trace/parts/jobs?${q}`);
      setTracePartJobs(res.data.jobs || []);
    } catch (err) {
      showToast(err.response?.data?.detail || "Could not load jobs for part", "error");
      setTracePartJobs([]);
    }
  };

  const fetchRmMapping = async () => {
    setRmMappingLoading(true);
    try {
      const res = await api.get("/admin/rm-mapping/pending");
      setRmMapping(res.data);
    } catch (err) {
      showToast(err.response?.data?.detail || "Could not load pending mappings", "error");
    }
    setRmMappingLoading(false);
  };

  const assignRmBatch = async (jobId, batchCode, subCode) => {
    try {
      const res = await api.post(`/admin/rm-mapping/${encodeURIComponent(jobId)}/assign`, {
        batch_code_base: batchCode,
        sub_code: subCode || "A",
      });
      showToast(`Mapped ${jobId} → ${batchCode}${res.data.replayed ? " (RM + lineage replayed)" : ""}`);
      fetchRmMapping();
    } catch (err) {
      showToast(err.response?.data?.detail || "Mapping failed", "error");
    }
  };

  const autoMatchAll = async () => {
    if (!window.confirm("Auto-apply the best RM batch for every manual-batch job? Manually-typed codes → best fuzzy match. Otherwise → oldest FIFO receipt with matching spec.")) return;
    setRmAutoMatching(true);
    try {
      const res = await api.post("/admin/rm-mapping/auto-match-all");
      const d = res.data;
      const parts = [`Auto-mapped ${d.mapped_count} job(s)`];
      if (d.fuzzy_matched) parts.push(`${d.fuzzy_matched} by fuzzy match`);
      if (d.fifo_matched) parts.push(`${d.fifo_matched} by FIFO`);
      if (d.skipped_no_candidate?.length) parts.push(`${d.skipped_no_candidate.length} skipped (no candidate — check spec)`);
      if (d.errors?.length) parts.push(`${d.errors.length} errors`);
      showToast(parts.join(". "));
      fetchRmMapping();
    } catch (err) {
      showToast(err.response?.data?.detail || "Auto-match failed", "error");
    }
    setRmAutoMatching(false);
  };

  const syncHistoricalData = async () => {
    if (!window.confirm("Refresh every historical production/inspection/FG record from current master data (part names, tools, RM, operator names)? This is safe and can be repeated.")) return;
    setSyncingHistorical(true);
    setSyncResult(null);
    try {
      const res = await api.post("/admin/sync-historical-data");
      setSyncResult(res.data);
      showToast(`Historical sync complete — ${res.data.total_docs_updated} record(s) updated.`);
    } catch (err) {
      showToast(err.response?.data?.detail || "Sync failed", "error");
    }
    setSyncingHistorical(false);
  };

  // ============== FG BATCHES ==============
  const fetchFgBatches = async () => {
    try {
      const res = await api.get("/fg-batches");
      setFgBatches(res.data || []);
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to load FG batches", "error");
    }
  };

  const linkInvoice = async (fgBatchNo, invoiceNo) => {
    try {
      await api.post(`/fg-batches/${encodeURIComponent(fgBatchNo)}/link-invoice`, { invoice_no: invoiceNo });
      showToast(`Linked ${fgBatchNo} → ${invoiceNo}`);
      fetchFgBatches();
    } catch (err) {
      showToast(err.response?.data?.detail || "Link failed", "error");
    }
  };

  const unlinkInvoice = async (fgBatchNo) => {
    if (!window.confirm(`Unlink invoice from ${fgBatchNo}? It will go back to READY status.`)) return;
    try {
      await api.delete(`/fg-batches/${encodeURIComponent(fgBatchNo)}/unlink-invoice`);
      showToast("Invoice unlinked");
      fetchFgBatches();
    } catch (err) {
      showToast(err.response?.data?.detail || "Unlink failed", "error");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-6">
      <Header title="Admin Panel" showBack backPath="/home" />

      {toast && (
        <div data-testid="toast" className={`fixed top-16 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl text-sm font-bold transition-all ${
          toast.type === "error" ? "bg-red-600 text-white" : "bg-[#15803D] text-white"
        }`}>
          {toast.type === "error" ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
          {toast.msg}
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Tabs */}
        <div className="flex flex-wrap gap-1 bg-white rounded-2xl border border-slate-200 p-1 mb-6 w-fit">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${
                  tab === t.id ? "bg-[#1A3560] text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Icon size={16} /> {t.label}{t.count !== null ? ` (${t.count})` : ""}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : (
          <>
            {tab === "users" && <UsersTab
              users={users} allMachines={machines}
              currentUserId={user?.user_id}
              onAdd={() => setShowUserModal(true)}
              onToggle={toggleActive}
              onResetPwd={(u) => { setSelectedUser(u); setShowPwdModal(true); }}
              onDelete={deleteUser}
              onChangeRole={changeRole}
              roles={dynamicRoles}
            />}
            {tab === "machines" && <MachinesTab
              machines={machines}
              onAdd={() => setShowMachineModal(true)}
              onUpdateStatus={updateMachineStatus}
              onDelete={deleteMachine}
              navigate={navigate}
            />}
            {tab === "tools" && <ToolsTab
              tools={tools}
              onAdd={openCreateTool}
              onEdit={openEditTool}
              onDelete={deleteTool}
              onPatch={patchTool} />}
            {tab === "parts" && <PartsTab
              parts={parts}
              onAdd={openCreatePart}
              onEdit={openEditPart}
              onDelete={deletePart}
            />}
            {tab === "inspection" && <InspectionTab
              rows={inspPlan} tools={tools}
              filter={inspToolFilter} setFilter={setInspToolFilter}
              onAdd={openCreateInsp} onEdit={openEditInsp} onDelete={deleteInsp}
            />}
            {tab === "audit" && <AuditTab logs={auditLogs} />}
            {tab === "sheets" && <SheetsTab status={sheetsStatus} syncing={syncing}
              onConnect={connectSheets} onSync={syncSheets} onDisconnect={disconnectSheets}
              onDownloadExcel={downloadExcel} />}
            {tab === "reports" && <ReportsTab status={reportsStatus} sending={sendingReport}
              onSendNow={sendReportNow} onPreviewPdf={previewPdf} onRefresh={fetchReportsStatus}
              hourlyStatus={hourlyStatus} hourlyPreview={hourlyPreview}
              sendingHourly={sendingHourly} onSendHourlyNow={sendHourlyNow} />}
            {tab === "import" && <ImportTab importing={importing} status={importStatus}
              onUpload={uploadHistorical} onDownloadTemplate={downloadImportTemplate}
              onSyncHistorical={syncHistoricalData} syncing={syncingHistorical}
              syncResult={syncResult} />}
            {tab === "trace" && <TraceTab jobId={traceJobId} setJobId={setTraceJobId}
              tracing={tracing} data={traceData} fgData={fgTraceData} onLookup={lookupTrace}
              parts={traceParts} selectedPart={traceSelectedPart} partJobs={tracePartJobs}
              onSelectPart={selectTracePart}
              rmMapping={rmMapping} showRmMapping={showRmMapping}
              setShowRmMapping={setShowRmMapping}
              rmMappingLoading={rmMappingLoading}
              onAssignRm={assignRmBatch}
              onAutoMatch={autoMatchAll}
              autoMatching={rmAutoMatching}
              onRefreshMapping={fetchRmMapping} />}
            {tab === "fg" && <FgBatchesTab batches={fgBatches} onLinkInvoice={linkInvoice}
              onUnlinkInvoice={unlinkInvoice} onTrace={(no) => { setTab("trace"); setTraceJobId(no); setTimeout(() => lookupTrace(no), 100); }} />}
            {tab === "shortfalls" && <ShortfallsTab
              shortfalls={shortfalls}
              dateFilter={shortfallDateFilter}
              setDateFilter={setShortfallDateFilter}
              onResolve={(s) => { setResolvingShortfall(s); setResolveAction("adjust"); setResolveNote(""); }}
              onLookup={lookupTrace} />}
            {tab === "iot" && <IotDevicesTab />}
            {tab === "bulk-tools" && <BulkToolsTab navigate={navigate} />}
            {tab === "reset" && <ResetTestDataTab showToast={showToast} />}
            {tab === "roles" && <RoleAccessTab />}
            {tab === "root-causes" && <RootCausesTab showToast={showToast} />}
          </>
        )}
      </div>

      {/* Add User Modal */}
      {showUserModal && (
        <Modal title="Add New User" onClose={() => setShowUserModal(false)}>
          <div className="space-y-3">
            <ModalField label="User ID *">
              <input data-testid="new-user-id" value={userForm.user_id} onChange={(e) => setUserForm((f) => ({ ...f, user_id: e.target.value.toUpperCase() }))}
                placeholder="e.g. OP003" className={INP} />
            </ModalField>
            <ModalField label="Full Name *">
              <input data-testid="new-user-name" value={userForm.full_name} onChange={(e) => setUserForm((f) => ({ ...f, full_name: e.target.value }))}
                placeholder="Full name" className={INP} />
            </ModalField>
            <ModalField label="Initial Password *">
              <div className="relative">
                <input data-testid="new-user-pwd" type="text"
                  value={userForm.password} onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="Min 4 characters" className={INP + " pr-10"} />
                <button type="button" onClick={() => {
                  const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
                  let p = ""; for (let i = 0; i < 8; i++) p += chars[Math.floor(Math.random() * chars.length)];
                  setUserForm((f) => ({ ...f, password: p }));
                }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#1A3560] hover:bg-slate-100 rounded"
                  title="Auto-generate">
                  <Wand2 size={16} />
                </button>
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Visible so you can note it down. Click the wand to auto-generate.</p>
            </ModalField>
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="Role *">
                <select value={userForm.role} onChange={(e) => setUserForm((f) => ({ ...f, role: e.target.value }))} className={INP}>
                  {dynamicRoles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </ModalField>
              <ModalField label="Department">
                <input value={userForm.department} onChange={(e) => setUserForm((f) => ({ ...f, department: e.target.value }))}
                  placeholder="e.g. Production" className={INP} />
              </ModalField>
            </div>
            {userForm.role === "Operator" && (
              <ModalField label="Assign Machine">
                <select value={userForm.machine_assigned} onChange={(e) => setUserForm((f) => ({ ...f, machine_assigned: e.target.value }))} className={INP}>
                  <option value="">-- None --</option>
                  {machines.map((m) => <option key={m.machine_code} value={m.machine_code}>{m.machine_code} ({m.tonnage}T)</option>)}
                </select>
              </ModalField>
            )}
            <button data-testid="save-user-btn" onClick={createUser}
              className="w-full h-12 bg-[#15803D] text-white font-bold rounded-xl active:scale-[0.98] transition-all mt-2">
              Create User / उपयोगकर्ता बनाएं
            </button>
          </div>
        </Modal>
      )}

      {/* Reset Password Modal */}
      {showPwdModal && selectedUser && (
        <Modal title={`Reset Password — ${selectedUser.user_id}`} onClose={closePwdModal}>
          {!resetSuccess ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Setting new password for <b>{selectedUser.full_name}</b></p>
              <ModalField label="New Password *">
                <div className="relative">
                  <input data-testid="new-pwd-input" type={showPwdText ? "text" : "password"}
                    value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min 4 characters" className={INP + " pr-20"} />
                  <button type="button" data-testid="toggle-pwd-visible"
                    onClick={() => setShowPwdText(!showPwdText)}
                    className="absolute right-10 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700"
                    title={showPwdText ? "Hide" : "Show"}>
                    {showPwdText ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <button type="button" data-testid="gen-pwd-btn"
                    onClick={generatePassword}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#1A3560] hover:bg-slate-100 rounded"
                    title="Auto-generate strong password">
                    <Wand2 size={16} />
                  </button>
                </div>
              </ModalField>
              <p className="text-[11px] text-slate-400">
                Tip: Click the wand icon to auto-generate a secure 8-character password.
              </p>
              <button data-testid="confirm-reset-btn" onClick={resetPassword}
                className="w-full h-12 bg-[#1A3560] text-white font-bold rounded-xl active:scale-[0.98] transition-all mt-2">
                Reset Password / पासवर्ड रीसेट करें
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <CheckCircle2 size={32} className="mx-auto text-[#15803D] mb-2" />
                <p className="font-extrabold text-[#15803D]">Password Reset Successful</p>
                <p className="text-xs text-slate-500 mt-1">For {resetSuccess.name} ({resetSuccess.user_id})</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">New Password</p>
                <div className="flex items-center gap-2 bg-white border border-slate-300 rounded-lg p-3">
                  <span data-testid="reset-success-pwd" className="flex-1 font-mono text-lg font-bold text-slate-900 select-all">
                    {resetSuccess.password}
                  </span>
                  <button data-testid="copy-pwd-btn" onClick={() => copyToClipboard(resetSuccess.password)}
                    className="flex items-center gap-1 bg-[#1A3560] text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-[#102340]">
                    <Copy size={12} /> Copy
                  </button>
                </div>
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-3">
                  ⚠ Note this password down before closing — it cannot be viewed again. Passwords are stored encrypted (one-way bcrypt) for security.
                </p>
              </div>
              <button onClick={closePwdModal}
                className="w-full h-11 bg-[#15803D] text-white font-bold rounded-xl active:scale-[0.98] transition-all">
                Done
              </button>
            </div>
          )}
        </Modal>
      )}

      {/* Add Machine Modal */}
      {showMachineModal && (
        <Modal title="Add New Machine" onClose={() => setShowMachineModal(false)}>
          <div className="space-y-3">
            <ModalField label="Machine Code *">
              <input data-testid="new-machine-code" value={machineForm.machine_code} onChange={(e) => setMachineForm((f) => ({ ...f, machine_code: e.target.value.toUpperCase() }))}
                placeholder="e.g. AMPL-PP-50" className={INP} />
            </ModalField>
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="Machine Type">
                <input value={machineForm.machine_type} onChange={(e) => setMachineForm((f) => ({ ...f, machine_type: e.target.value }))}
                  placeholder="Power Press" className={INP} />
              </ModalField>
              <ModalField label="Tonnage (T) *">
                <input type="number" value={machineForm.tonnage} onChange={(e) => setMachineForm((f) => ({ ...f, tonnage: e.target.value }))}
                  placeholder="100" className={INP} min="1" />
              </ModalField>
            </div>
            <ModalField label="Notes">
              <input value={machineForm.notes} onChange={(e) => setMachineForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Optional notes" className={INP} />
            </ModalField>
            <button data-testid="save-machine-btn" onClick={createMachine}
              className="w-full h-12 bg-[#15803D] text-white font-bold rounded-xl active:scale-[0.98] transition-all mt-2">
              Add Machine / मशीन जोड़ें
            </button>
          </div>
        </Modal>
      )}

      {/* Tool Modal */}
      {showToolModal && (
        <Modal title={toolEditing ? `Edit Tool — ${toolEditing}` : "Add New Tool"} onClose={() => setShowToolModal(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="Tool No *">
                <input data-testid="tool-no" disabled={!!toolEditing} value={toolForm.tool_no}
                  onChange={(e) => setToolForm((f) => ({ ...f, tool_no: e.target.value }))} placeholder="e.g. 31" className={INP} />
              </ModalField>
              <ModalField label="Tool Name">
                <input value={toolForm.tool_name} onChange={(e) => setToolForm((f) => ({ ...f, tool_name: e.target.value }))} placeholder="Draw Die" className={INP} />
              </ModalField>
              <ModalField label="Part Name *">
                <input data-testid="tool-part-name" value={toolForm.part_name} onChange={(e) => setToolForm((f) => ({ ...f, part_name: e.target.value }))} placeholder="Frame 6 inch" className={INP} />
              </ModalField>
              <ModalField label="Part No *">
                <input value={toolForm.part_no} onChange={(e) => setToolForm((f) => ({ ...f, part_no: e.target.value }))} placeholder="11001..." className={INP} />
              </ModalField>
              <ModalField label="Operation *">
                <input value={toolForm.operation} onChange={(e) => setToolForm((f) => ({ ...f, operation: e.target.value }))} placeholder="Draw / Punching" className={INP} />
              </ModalField>
              <ModalField label="Customer">
                <input value={toolForm.customer} onChange={(e) => setToolForm((f) => ({ ...f, customer: e.target.value }))} className={INP} />
              </ModalField>
              <ModalField label="RM Grade">
                <input value={toolForm.rm_grade} onChange={(e) => setToolForm((f) => ({ ...f, rm_grade: e.target.value }))} placeholder="CRCA-D" className={INP} />
              </ModalField>
              <ModalField label="RM Code">
                <input value={toolForm.rm_code} onChange={(e) => setToolForm((f) => ({ ...f, rm_code: e.target.value }))} className={INP} />
              </ModalField>
              <ModalField label="Thickness (mm)">
                <input type="number" step="0.01" value={toolForm.thickness_mm} onChange={(e) => setToolForm((f) => ({ ...f, thickness_mm: e.target.value }))} className={INP} />
              </ModalField>
              <ModalField label="Width (mm)">
                <input type="number" step="0.01" value={toolForm.width_mm} onChange={(e) => setToolForm((f) => ({ ...f, width_mm: e.target.value }))} className={INP} />
              </ModalField>
              <ModalField label="Cavities">
                <input type="number" value={toolForm.no_of_cavities} onChange={(e) => setToolForm((f) => ({ ...f, no_of_cavities: e.target.value }))} className={INP} />
              </ModalField>
              <ModalField label="Min Tonnage (T)">
                <input type="number" value={toolForm.min_tonnage_t} onChange={(e) => setToolForm((f) => ({ ...f, min_tonnage_t: e.target.value }))} className={INP} />
              </ModalField>
              <ModalField label="Strokes/min">
                <input type="number" value={toolForm.strokes_per_min} onChange={(e) => setToolForm((f) => ({ ...f, strokes_per_min: e.target.value }))} className={INP} />
              </ModalField>
              <ModalField label="Target/Day">
                <input type="number" value={toolForm.target_per_day} onChange={(e) => setToolForm((f) => ({ ...f, target_per_day: e.target.value }))} className={INP} />
              </ModalField>
              <ModalField label="Operation Seq (1, 2, …)">
                <input data-testid="tool-op-seq" type="number" min="1" value={toolForm.operation_seq} onChange={(e) => setToolForm((f) => ({ ...f, operation_seq: e.target.value }))} placeholder="auto from [X/Y]" className={INP} />
              </ModalField>
              <ModalField label="Total Operations (Y)">
                <input data-testid="tool-total-ops" type="number" min="1" value={toolForm.total_operations} onChange={(e) => setToolForm((f) => ({ ...f, total_operations: e.target.value }))} placeholder="auto from [X/Y]" className={INP} />
              </ModalField>
              <ModalField label="Gross Weight (g) — first-op only">
                <input data-testid="tool-gross-weight" type="number" step="0.01" min="0" value={toolForm.gross_weight_g} onChange={(e) => setToolForm((f) => ({ ...f, gross_weight_g: e.target.value }))} placeholder="e.g. 18.5" className={INP} />
              </ModalField>
            </div>
            <button data-testid="save-tool-btn" onClick={saveTool}
              className="w-full h-12 bg-[#15803D] text-white font-bold rounded-xl active:scale-[0.98] transition-all mt-2">
              {toolEditing ? "Update Tool / टूल अपडेट" : "Add Tool / टूल जोड़ें"}
            </button>
          </div>
        </Modal>
      )}

      {/* Part Modal */}
      {showPartModal && (
        <Modal title={partEditing ? `Edit Part — ${partEditing}` : "Add New Part"} onClose={() => setShowPartModal(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="Internal Part No * (AMPL-…)">
                <input data-testid="part-internal-no" disabled={!!partEditing} value={partForm.internal_part_no}
                  onChange={(e) => setPartForm((f) => ({ ...f, internal_part_no: e.target.value }))}
                  placeholder="AMPL-XXX" className={INP} />
              </ModalField>
              <ModalField label="Part No *">
                <input data-testid="part-no" value={partForm.part_no}
                  onChange={(e) => setPartForm((f) => ({ ...f, part_no: e.target.value }))}
                  placeholder="Customer part no" className={INP} />
              </ModalField>
              <ModalField label="Part Name *">
                <input data-testid="part-name" value={partForm.part_name}
                  onChange={(e) => setPartForm((f) => ({ ...f, part_name: e.target.value }))}
                  placeholder="Frame 6 inch" className={INP} />
              </ModalField>
              <ModalField label="Customer">
                <input value={partForm.customer}
                  onChange={(e) => setPartForm((f) => ({ ...f, customer: e.target.value }))}
                  placeholder="M&M / Tata / …" className={INP} />
              </ModalField>
              <ModalField label="Customer Drawing No">
                <input value={partForm.customer_drawing_no}
                  onChange={(e) => setPartForm((f) => ({ ...f, customer_drawing_no: e.target.value }))}
                  placeholder="DWG-…" className={INP} />
              </ModalField>
              <ModalField label="RM Grade">
                <input value={partForm.rm_grade}
                  onChange={(e) => setPartForm((f) => ({ ...f, rm_grade: e.target.value }))}
                  placeholder="CRCA / HRCA / Aluminium" className={INP} />
              </ModalField>
              <ModalField label="RM Thickness (mm)">
                <input type="number" step="0.01" value={partForm.rm_thickness}
                  onChange={(e) => setPartForm((f) => ({ ...f, rm_thickness: e.target.value }))}
                  placeholder="0.5" className={INP} />
              </ModalField>
              <ModalField label="RM Width">
                <input value={partForm.rm_width}
                  onChange={(e) => setPartForm((f) => ({ ...f, rm_width: e.target.value }))}
                  placeholder="37 or 37-40" className={INP} />
              </ModalField>
            </div>
            <button data-testid="save-part-btn" onClick={savePart}
              className="w-full h-12 bg-[#15803D] text-white font-bold rounded-xl active:scale-[0.98] transition-all mt-2">
              {partEditing ? "Update Part / पार्ट अपडेट" : "Add Part / पार्ट जोड़ें"}
            </button>
          </div>
        </Modal>
      )}

      {/* Inspection Modal */}
      {showInspModal && (
        <Modal title={inspEditing ? `Edit Insp Row — ${inspEditing.tool_no}#${inspEditing.s_no}` : "Add Inspection Row"} onClose={() => setShowInspModal(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="Tool No *">
                <select disabled={!!inspEditing} value={inspForm.tool_no}
                  onChange={(e) => setInspForm((f) => ({ ...f, tool_no: e.target.value }))} className={INP}>
                  <option value="">-- Select --</option>
                  {tools.filter(t => t.active === "Y").map((t) => (
                    <option key={t.tool_no} value={t.tool_no}>{t.tool_no} — {t.part_name}</option>
                  ))}
                </select>
              </ModalField>
              <ModalField label="S.No *">
                <input data-testid="insp-sno" type="number" disabled={!!inspEditing} value={inspForm.s_no}
                  onChange={(e) => setInspForm((f) => ({ ...f, s_no: e.target.value }))} className={INP} />
              </ModalField>
            </div>
            <ModalField label="Part No">
              <input value={inspForm.part_no} onChange={(e) => setInspForm((f) => ({ ...f, part_no: e.target.value }))} placeholder="Auto from tool" className={INP} />
            </ModalField>
            <ModalField label="Dimension Description *">
              <input data-testid="insp-dim" value={inspForm.dimension_description} onChange={(e) => setInspForm((f) => ({ ...f, dimension_description: e.target.value }))}
                placeholder="e.g. H-6.0±0.5" className={INP} />
            </ModalField>
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="Check Type">
                <select value={inspForm.check_type} onChange={(e) => setInspForm((f) => ({ ...f, check_type: e.target.value }))} className={INP}>
                  {CHECK_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </ModalField>
              <ModalField label="Instrument">
                <input value={inspForm.instrument} onChange={(e) => setInspForm((f) => ({ ...f, instrument: e.target.value }))} placeholder="Caliper / DVC" className={INP} />
              </ModalField>
              {inspForm.check_type === "Measured" && (
                <>
                  <ModalField label="Nominal">
                    <input type="number" step="0.001" value={inspForm.nominal} onChange={(e) => setInspForm((f) => ({ ...f, nominal: e.target.value }))} className={INP} />
                  </ModalField>
                  <ModalField label="Tol +">
                    <input type="number" step="0.001" value={inspForm.tolerance_plus} onChange={(e) => setInspForm((f) => ({ ...f, tolerance_plus: e.target.value }))} className={INP} />
                  </ModalField>
                  <ModalField label="Tol −">
                    <input type="number" step="0.001" value={inspForm.tolerance_minus} onChange={(e) => setInspForm((f) => ({ ...f, tolerance_minus: e.target.value }))} className={INP} />
                  </ModalField>
                  <ModalField label="LSL">
                    <input type="number" step="0.001" value={inspForm.lsl} onChange={(e) => setInspForm((f) => ({ ...f, lsl: e.target.value }))} className={INP} />
                  </ModalField>
                  <ModalField label="USL">
                    <input type="number" step="0.001" value={inspForm.usl} onChange={(e) => setInspForm((f) => ({ ...f, usl: e.target.value }))} className={INP} />
                  </ModalField>
                  <ModalField label="Multi-point Count">
                    <input type="number" value={inspForm.multi_point_count} onChange={(e) => setInspForm((f) => ({ ...f, multi_point_count: e.target.value }))} className={INP} />
                  </ModalField>
                </>
              )}
              <ModalField label="Cavities">
                <input type="number" value={inspForm.cavities} onChange={(e) => setInspForm((f) => ({ ...f, cavities: e.target.value }))} className={INP} />
              </ModalField>
              <ModalField label="First Sample Size">
                <input type="number" value={inspForm.first_sample_size} onChange={(e) => setInspForm((f) => ({ ...f, first_sample_size: e.target.value }))} className={INP} />
              </ModalField>
              <ModalField label="Periodic Sample">
                <input type="number" value={inspForm.periodic_sample_size} onChange={(e) => setInspForm((f) => ({ ...f, periodic_sample_size: e.target.value }))} className={INP} />
              </ModalField>
              <ModalField label="Periodic Hours">
                <input type="number" value={inspForm.periodic_interval_hours} onChange={(e) => setInspForm((f) => ({ ...f, periodic_interval_hours: e.target.value }))} className={INP} />
              </ModalField>
            </div>
            <ModalField label="Inspector Note">
              <input value={inspForm.inspector_note} onChange={(e) => setInspForm((f) => ({ ...f, inspector_note: e.target.value }))} className={INP} />
            </ModalField>
            <button data-testid="save-insp-btn" onClick={saveInsp}
              className="w-full h-12 bg-[#15803D] text-white font-bold rounded-xl active:scale-[0.98] transition-all mt-2">
              {inspEditing ? "Update Row" : "Add Row"}
            </button>
          </div>
        </Modal>
      )}

      {resolvingShortfall && (
        <Modal title={`Resolve Shortfall — ${resolvingShortfall.job_id}`} onClose={() => setResolvingShortfall(null)}>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm">
              <p className="text-xs text-amber-700 mb-1 font-bold uppercase tracking-widest">Shortfall details</p>
              <p className="text-amber-900">
                <b>{resolvingShortfall.part_name}</b> at op {resolvingShortfall.operation_seq} of {resolvingShortfall.total_operations}
              </p>
              <p className="text-xs text-amber-800 mt-1">
                Available at start: <b>{resolvingShortfall.wip_available_at_start ?? 0}</b> &nbsp;•&nbsp;
                Shortfall: <b>{resolvingShortfall.wip_shortfall_qty ?? "—"} pcs</b> &nbsp;•&nbsp;
                Source op: <b>{(resolvingShortfall.operation_seq || 1) - 1}</b>
              </p>
            </div>
            <ModalField label="Resolution action">
              <div className="space-y-2">
                <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${resolveAction === "adjust" ? "border-[#1A3560] bg-blue-50" : "border-slate-200"}`}>
                  <input
                    data-testid="resolve-action-adjust"
                    type="radio"
                    name="resolve-action"
                    value="adjust"
                    checked={resolveAction === "adjust"}
                    onChange={() => setResolveAction("adjust")}
                    className="mt-1"
                  />
                  <div className="flex-1 text-sm">
                    <p className="font-bold">Adjust — back-fill the missing WIP</p>
                    <p className="text-xs text-slate-500">
                      Records {resolvingShortfall.wip_shortfall_qty || 0} pcs as having flowed in/out at op {(resolvingShortfall.operation_seq || 1) - 1}.
                      Use this when you confirm the parts physically existed (the previous op's Stage-2 was just submitted late).
                    </p>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${resolveAction === "dismiss" ? "border-slate-700 bg-slate-50" : "border-slate-200"}`}>
                  <input
                    data-testid="resolve-action-dismiss"
                    type="radio"
                    name="resolve-action"
                    value="dismiss"
                    checked={resolveAction === "dismiss"}
                    onChange={() => setResolveAction("dismiss")}
                    className="mt-1"
                  />
                  <div className="flex-1 text-sm">
                    <p className="font-bold">Dismiss — acknowledge without adjusting WIP</p>
                    <p className="text-xs text-slate-500">
                      Just clears the flag. Use when reconciliation will happen outside the system or the shortfall is being investigated.
                    </p>
                  </div>
                </label>
              </div>
            </ModalField>
            <ModalField label="Note (audit trail)">
              <textarea
                data-testid="resolve-note"
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
                placeholder="e.g. Operator confirmed 188 pcs on the machine — previous op's Stage-2 closed by Suresh at 19:45."
                rows={3}
                className={INP}
              />
            </ModalField>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setResolvingShortfall(null)}
                className="h-11 px-4 rounded-xl border border-slate-300 text-slate-700 font-bold text-sm hover:bg-slate-50">
                Cancel
              </button>
              <button
                data-testid="resolve-submit-btn"
                onClick={submitResolve}
                className={`h-11 px-5 rounded-xl text-white font-bold text-sm active:scale-[0.97] ${resolveAction === "adjust" ? "bg-[#1A3560] hover:bg-[#102340]" : "bg-slate-700 hover:bg-slate-800"}`}
              >
                {resolveAction === "adjust" ? "Adjust WIP & Resolve" : "Dismiss & Resolve"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ============== TAB COMPONENTS ==============

const USER_ROLES_FALLBACK = ["Main Admin", "Supervisor", "Operator", "Inspector", "Stores"];

const UsersTab = ({ users, allMachines, currentUserId, onAdd, onToggle, onResetPwd, onDelete, onChangeRole, roles }) => (
  <div data-testid="users-tab-content">
    <div className="flex justify-between items-center mb-4">
      <div>
        <h2 className="font-extrabold text-slate-900">User Management</h2>
        <p className="text-xs text-slate-400">Add operators, inspectors and other users here. Click any role badge to change a user's role.</p>
      </div>
      <button data-testid="add-user-btn" onClick={onAdd}
        className="flex items-center gap-2 bg-[#1A3560] text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-[#102340] active:scale-[0.98] transition-all">
        <Plus size={16} /> Add User
      </button>
    </div>
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["User ID", "Name", "Role", "Department", "Machine", "Active", "Actions"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={u.user_id} className={`border-t border-slate-100 ${i % 2 === 0 ? "" : "bg-slate-50/40"}`}>
                <td className="px-4 py-3 font-mono text-sm font-bold text-[#1A3560]">{u.user_id}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{u.full_name}</td>
                <td className="px-4 py-3">
                  <select
                    data-testid={`role-select-${u.user_id}`}
                    value={u.role}
                    onChange={(e) => onChangeRole(u, e.target.value)}
                    className="text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-1 hover:bg-blue-100 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
                  >
                    {(roles && roles.length ? roles : USER_ROLES_FALLBACK).map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">{u.department || "—"}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{u.machine_assigned || "—"}</td>
                <td className="px-4 py-3">
                  <button data-testid={`toggle-active-${u.user_id}`} onClick={() => onToggle(u.user_id, u.active)}
                    className={`w-12 h-6 rounded-full transition-all relative ${u.active === "Y" ? "bg-[#15803D]" : "bg-slate-300"}`}>
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${u.active === "Y" ? "left-6" : "left-0.5"}`} />
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`reset-pwd-${u.user_id}`} onClick={() => onResetPwd(u)}
                      className="flex items-center gap-1 text-xs text-slate-600 hover:text-[#1A3560] font-medium bg-slate-100 hover:bg-slate-200 px-2 py-1.5 rounded-lg transition-all">
                      <KeyRound size={12} /> Reset Pwd
                    </button>
                    {u.user_id !== currentUserId && (
                      <button data-testid={`delete-user-${u.user_id}`} onClick={() => onDelete(u.user_id, u.full_name)}
                        className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-all" title="Delete user">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
      <p className="text-sm font-bold text-blue-800 mb-1">How to add Operators / Inspectors</p>
      <p className="text-xs text-blue-700">Click "Add User" above. Set Role = <b>Operator</b> and assign a machine. Once added, the operator will appear in the Production Stage 1 dropdown automatically. To change an existing user's role, click their role badge in the table above.</p>
    </div>
  </div>
);

const MachinesTab = ({ machines, onAdd, onUpdateStatus, onDelete, navigate }) => (
  <div data-testid="machines-tab-content">
    <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
      <div>
        <h2 className="font-extrabold text-slate-900">Machine Master Data</h2>
        <p className="text-xs text-slate-400">Update machine status, add new machines, or retire ones removed from the shop floor.</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          data-testid="machines-bulk-import-btn"
          onClick={() => navigate("/admin/bulk-machine-setup")}
          className="flex items-center gap-2 bg-white hover:bg-slate-50 text-[#1A3560] border border-slate-300 px-4 py-2.5 rounded-xl text-sm font-bold active:scale-[0.98] transition-all"
          title="Bulk create / update machines via CSV"
        >
          <Upload size={16} /> Bulk Import (CSV)
        </button>
        <button data-testid="add-machine-btn" onClick={onAdd}
          className="flex items-center gap-2 bg-[#1A3560] text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-[#102340] active:scale-[0.98] transition-all">
          <Plus size={16} /> Add Machine
        </button>
      </div>
    </div>
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            {["Machine Code", "Type", "Tonnage", "Status", "Change Status", "Actions"].map((h) => (
              <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {machines.map((m, i) => {
            const retired = m.status === "Retired" || m.active === "N";
            return (
              <tr key={m.machine_code} className={`border-t border-slate-100 ${retired ? "opacity-60" : ""} ${i % 2 === 0 ? "" : "bg-slate-50/40"}`}>
                <td className="px-4 py-3 font-bold text-[#1A3560]">{m.machine_code}</td>
                <td className="px-4 py-3 text-slate-600">{m.machine_type}</td>
                <td className="px-4 py-3 font-bold">{m.tonnage}T</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                    m.status === "Active" ? "bg-green-100 text-green-700" :
                    m.status === "Maintenance" ? "bg-red-100 text-red-700" :
                    m.status === "Retired" ? "bg-slate-200 text-slate-500" :
                    "bg-slate-100 text-slate-500"
                  }`}>{m.status}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="relative inline-block">
                    <select data-testid={`machine-status-${m.machine_code}`} value={m.status}
                      onChange={(e) => onUpdateStatus(m.machine_code, e.target.value)}
                      disabled={retired}
                      className="h-8 pl-2 pr-7 rounded-lg border border-slate-300 text-xs font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#1A3560] appearance-none disabled:bg-slate-100 disabled:text-slate-400">
                      {MACHINE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </td>
                <td className="px-4 py-3">
                  {!retired ? (
                    <button
                      data-testid={`retire-machine-${m.machine_code}`}
                      onClick={() => onDelete(m.machine_code, false)}
                      className="text-xs font-extrabold text-red-700 hover:bg-red-50 border border-red-200 rounded-lg px-2 py-1 inline-flex items-center gap-1"
                      title="Retire — hides from selection, preserves history"
                    >
                      <Trash2 size={11} /> Retire
                    </button>
                  ) : (
                    <button
                      data-testid={`hard-delete-machine-${m.machine_code}`}
                      onClick={() => onDelete(m.machine_code, true)}
                      className="text-xs font-extrabold text-red-800 hover:bg-red-100 border border-red-300 rounded-lg px-2 py-1 inline-flex items-center gap-1"
                      title="Delete permanently — allowed only if no history"
                    >
                      <Trash2 size={11} /> Delete Forever
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

const ToolsTab = ({ tools, onAdd, onEdit, onDelete, onPatch }) => {
  const [filter, setFilter] = useState("all"); // all | needs-weight | first-op
  const [search, setSearch] = useState("");

  const visible = tools.filter((t) => {
    if (filter === "needs-weight" && !(t.is_first_op && (!t.gross_weight_g || t.gross_weight_g <= 0))) return false;
    if (filter === "first-op" && !t.is_first_op) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!`${t.tool_no} ${t.tool_name || ""} ${t.part_name || ""} ${t.part_no || ""}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const needsWeightCount = tools.filter((t) => t.is_first_op && (!t.gross_weight_g || t.gross_weight_g <= 0) && t.active === "Y").length;

  return (
    <div data-testid="tools-tab-content">
      <div className="flex justify-between items-start gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-extrabold text-slate-900">Tool Master Data</h2>
          <p className="text-xs text-slate-400">Add/edit dies and tools used in production. Click any <b>Gross Weight (g)</b> cell on a first-op tool to edit inline.</p>
        </div>
        <button data-testid="add-tool-btn" onClick={onAdd}
          className="flex items-center gap-2 bg-[#1A3560] text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-[#102340] active:scale-[0.98] transition-all">
          <Plus size={16} /> Add Tool
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <input
          data-testid="tool-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by tool no, name, part…"
          className="h-10 px-3 rounded-lg border border-slate-300 text-sm flex-1 min-w-[200px] focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
        />
        <FilterChip label={`All (${tools.length})`} active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all" />
        <FilterChip label={`First-op (${tools.filter(t => t.is_first_op).length})`} active={filter === "first-op"} onClick={() => setFilter("first-op")} testId="filter-first-op" />
        <FilterChip
          label={<span>Needs Gross Weight {needsWeightCount > 0 && <span className="ml-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{needsWeightCount}</span>}</span>}
          active={filter === "needs-weight"}
          onClick={() => setFilter("needs-weight")}
          tone={needsWeightCount > 0 ? "danger" : "default"}
          testId="filter-needs-weight"
        />
      </div>

      {needsWeightCount > 0 && filter !== "needs-weight" && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 max-w-3xl">
          <b>{needsWeightCount}</b> first-op tool{needsWeightCount > 1 ? "s" : ""} {needsWeightCount > 1 ? "have" : "has"} no Gross Weight set — RM auto-deduction is disabled for {needsWeightCount > 1 ? "them" : "it"}.
          <button onClick={() => setFilter("needs-weight")} className="ml-2 underline font-bold">Show only those</button>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Tool", "Part", "Op", "Cav", "Min T", "Gross Wt (g)", "Status", "Actions"].map((h) => (
                  <th key={h} className="text-left px-2 py-3 text-xs font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10 text-slate-400">No tools match the current filter.</td></tr>
              ) : visible.map((t, i) => (
                <tr key={t.tool_no} className={`border-t border-slate-100 ${t.active === "N" ? "opacity-50" : ""} ${i % 2 === 0 ? "" : "bg-slate-50/40"}`}>
                  <td className="px-2 py-3">
                    <div className="font-bold text-[#1A3560]">#{t.tool_no}</div>
                    <div className="text-xs text-slate-500 max-w-[160px] truncate" title={t.tool_name}>{t.tool_name}</div>
                  </td>
                  <td className="px-2 py-3">
                    <div className="font-medium text-slate-900 max-w-[180px] truncate" title={t.part_name}>{t.part_name}</div>
                    <div className="text-xs text-slate-400 font-mono">{t.part_no}</div>
                  </td>
                  <td className="px-2 py-3">
                    <OpBadge tool={t} />
                  </td>
                  <td className="px-2 py-3 text-center">{t.no_of_cavities}</td>
                  <td className="px-2 py-3 font-bold whitespace-nowrap">{t.min_tonnage_t}T</td>
                  <td className="px-2 py-3">
                    <InlineGrossWeight tool={t} onPatch={onPatch} />
                  </td>
                  <td className="px-2 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-bold ${t.active === "Y" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                      {t.active === "Y" ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-2 py-3">
                    <div className="flex items-center gap-2">
                      <button data-testid={`edit-tool-${t.tool_no}`} onClick={() => onEdit(t)}
                        className="inline-flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg text-xs font-bold active:scale-95 transition-all" title="Edit tool">
                        <Pencil size={12} /> Edit
                      </button>
                      {t.active === "Y" && (
                        <button data-testid={`delete-tool-${t.tool_no}`} onClick={() => onDelete(t.tool_no)}
                          className="inline-flex items-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 px-2.5 py-1.5 rounded-lg text-xs font-bold active:scale-95 transition-all" title="Deactivate tool">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const PartsTab = ({ parts, onAdd, onEdit, onDelete }) => {
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const visible = parts.filter((p) => {
    if (!showInactive && p.active === "N") return false;
    if (search) {
      const q = search.toLowerCase();
      if (!`${p.internal_part_no} ${p.part_no || ""} ${p.part_name || ""} ${p.customer || ""}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div data-testid="parts-tab-content">
      <div className="flex justify-between items-start gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-extrabold text-slate-900">Parts Master Data</h2>
          <p className="text-xs text-slate-400">Add / edit customer parts. Internal Part No must start with <code className="bg-slate-100 px-1 rounded">AMPL-</code> and is the unique key.</p>
        </div>
        <button data-testid="add-part-btn" onClick={onAdd}
          className="flex items-center gap-2 bg-[#1A3560] text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-[#102340] active:scale-[0.98] transition-all">
          <Plus size={16} /> Add Part
        </button>
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-4">
        <input
          data-testid="part-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by internal no, part no, name, customer…"
          className="h-10 px-3 rounded-lg border border-slate-300 text-sm flex-1 min-w-[200px] focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
        />
        <label className="flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer select-none">
          <input
            data-testid="parts-show-inactive"
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-[#1A3560] focus:ring-[#1A3560]"
          />
          Show inactive
        </label>
        <span className="text-xs bg-slate-100 text-slate-700 px-3 py-1.5 rounded-lg font-bold">
          {visible.length} of {parts.length}
        </span>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Internal No", "Customer Part", "Part Name", "Customer", "RM Grade", "Thk / Width", "Drawing No", "Status", ""].map((h) => (
                  <th key={h} className="text-left px-3 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-10 text-slate-400">
                  {parts.length === 0 ? "No parts yet — click 'Add Part' to create the first one." : "No parts match the current filter."}
                </td></tr>
              ) : visible.map((p, i) => (
                <tr key={p.internal_part_no} className={`border-t border-slate-100 ${p.active === "N" ? "opacity-50" : ""} ${i % 2 === 0 ? "" : "bg-slate-50/40"}`}>
                  <td className="px-3 py-3 font-mono text-[#1A3560] font-bold">{p.internal_part_no}</td>
                  <td className="px-3 py-3 font-mono text-xs text-slate-700">{p.part_no || "—"}</td>
                  <td className="px-3 py-3">
                    <div className="font-medium text-slate-900 max-w-[260px] truncate" title={p.part_name}>{p.part_name || "—"}</div>
                  </td>
                  <td className="px-3 py-3 text-slate-700">{p.customer || "—"}</td>
                  <td className="px-3 py-3 text-slate-700">{p.rm_grade || "—"}</td>
                  <td className="px-3 py-3 text-xs text-slate-600">
                    {p.rm_thickness ? `${p.rm_thickness} mm` : "—"}
                    {p.rm_width ? ` × ${p.rm_width}` : ""}
                  </td>
                  <td className="px-3 py-3 text-xs text-slate-500 font-mono max-w-[140px] truncate" title={p.customer_drawing_no}>{p.customer_drawing_no || "—"}</td>
                  <td className="px-3 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-bold ${p.active !== "N" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                      {p.active !== "N" ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <button data-testid={`edit-part-${p.internal_part_no}`} onClick={() => onEdit(p)}
                        className="inline-flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg text-xs font-bold active:scale-95 transition-all" title="Edit part">
                        <Pencil size={12} /> Edit
                      </button>
                      {p.active !== "N" && (
                        <button data-testid={`delete-part-${p.internal_part_no}`} onClick={() => onDelete(p.internal_part_no)}
                          className="inline-flex items-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 px-2.5 py-1.5 rounded-lg text-xs font-bold active:scale-95 transition-all" title="Deactivate part">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


const FilterChip = ({ label, active, onClick, tone = "default", testId }) => {
  const base = "px-3 h-10 rounded-lg text-sm font-bold border transition-all active:scale-[0.97]";
  const styles = active
    ? (tone === "danger" ? "bg-red-600 text-white border-red-600" : "bg-[#1A3560] text-white border-[#1A3560]")
    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50";
  return <button data-testid={testId} onClick={onClick} className={`${base} ${styles}`}>{label}</button>;
};

const OpBadge = ({ tool }) => {
  const seq = tool.operation_seq || 1;
  const total = tool.total_operations || 1;
  const isFirst = tool.is_first_op ?? (seq === 1);
  const isLast = tool.is_last_op ?? (seq === total);
  const cls = isFirst
    ? "bg-amber-100 text-amber-800 border-amber-300"
    : isLast
      ? "bg-emerald-100 text-emerald-800 border-emerald-300"
      : "bg-slate-100 text-slate-700 border-slate-300";
  const label = isFirst ? "FIRST" : isLast ? "LAST" : "MID";
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-bold border ${cls}`}>
      <span>{seq}/{total}</span>
      <span className="text-[10px] opacity-70">{label}</span>
    </div>
  );
};

const InlineGrossWeight = ({ tool, onPatch }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(tool.gross_weight_g ?? ""));
  const [saving, setSaving] = useState(false);

  // Keep state in sync if tool prop changes externally
  useEffect(() => { setValue(String(tool.gross_weight_g ?? "")); }, [tool.gross_weight_g]);

  const showsValue = tool.gross_weight_g && tool.gross_weight_g > 0;
  const requiresValue = tool.is_first_op;

  const commit = async () => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) {
      setValue(String(tool.gross_weight_g ?? ""));
      setEditing(false);
      return;
    }
    if (num === (tool.gross_weight_g || 0)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    await onPatch(tool.tool_no, { gross_weight_g: num });
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setEditing(false); setValue(String(tool.gross_weight_g ?? "")); } }}
          data-testid={`gw-input-${tool.tool_no}`}
          className="w-20 h-9 px-2 rounded-lg border-2 border-[#1A3560] text-sm font-bold focus:outline-none"
        />
        <span className="text-xs text-slate-400">g</span>
      </div>
    );
  }

  // Non-first-op tools don't need gross weight
  if (!requiresValue) {
    return <span className="text-xs text-slate-300">— n/a</span>;
  }

  return (
    <button
      data-testid={`gw-cell-${tool.tool_no}`}
      onClick={() => setEditing(true)}
      disabled={saving}
      className={`group flex items-center gap-2 px-2 py-1.5 rounded-lg border-2 border-dashed transition-all ${
        showsValue
          ? "border-transparent hover:border-slate-300 text-slate-900"
          : "border-red-300 bg-red-50 hover:bg-red-100 text-red-700"
      }`}
    >
      {showsValue ? (
        <>
          <span className="font-bold text-sm">{tool.gross_weight_g}</span>
          <span className="text-xs text-slate-400">g</span>
          <Pencil size={11} className="text-slate-300 group-hover:text-slate-500" />
        </>
      ) : (
        <>
          <span className="text-xs font-bold">⚠ Set weight</span>
          <Pencil size={11} />
        </>
      )}
    </button>
  );
};

const InspectionTab = ({ rows, tools, filter, setFilter, onAdd, onEdit, onDelete }) => (
  <div data-testid="inspection-tab-content">
    <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
      <div>
        <h2 className="font-extrabold text-slate-900">Inspection Plan</h2>
        <p className="text-xs text-slate-400">Define dimensions to inspect for each tool</p>
      </div>
      <div className="flex items-center gap-2">
        <select data-testid="insp-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="h-10 px-3 rounded-xl border border-slate-300 text-sm bg-white">
          <option value="">All Tools</option>
          {tools.filter(t => t.active === "Y").map((t) => (
            <option key={t.tool_no} value={t.tool_no}>#{t.tool_no} — {t.part_name}</option>
          ))}
        </select>
        <button data-testid="add-insp-btn" onClick={onAdd}
          className="flex items-center gap-2 bg-[#1A3560] text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-[#102340] active:scale-[0.98] transition-all">
          <Plus size={16} /> Add Row
        </button>
      </div>
    </div>
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Tool", "S.No", "Dimension", "Type", "Nominal", "LSL", "USL", "Instrument", "Active", "Actions"].map((h) => (
                <th key={h} className="text-left px-3 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-8 text-slate-400">No inspection rows. Click "Add Row" to create one.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={`${r.tool_no}-${r.s_no}`} className={`border-t border-slate-100 ${r.active === "N" ? "opacity-50" : ""} ${i % 2 === 0 ? "" : "bg-slate-50/40"}`}>
                <td className="px-3 py-3 font-bold text-[#1A3560]">#{r.tool_no}</td>
                <td className="px-3 py-3 font-bold">{r.s_no}</td>
                <td className="px-3 py-3 text-slate-900">{r.dimension_description}</td>
                <td className="px-3 py-3">
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-full font-bold">{r.check_type}</span>
                </td>
                <td className="px-3 py-3">{r.nominal ?? "—"}</td>
                <td className="px-3 py-3">{r.lsl ?? "—"}</td>
                <td className="px-3 py-3">{r.usl ?? "—"}</td>
                <td className="px-3 py-3 text-slate-600 text-xs">{r.instrument || "—"}</td>
                <td className="px-3 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-bold ${r.active === "Y" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {r.active === "Y" ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <button data-testid={`edit-insp-${r.tool_no}-${r.s_no}`} onClick={() => onEdit(r)}
                      className="inline-flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg text-xs font-bold active:scale-95 transition-all" title="Edit">
                      <Pencil size={12} /> Edit
                    </button>
                    <button data-testid={`delete-insp-${r.tool_no}-${r.s_no}`} onClick={() => onDelete(r.tool_no, r.s_no)}
                      className="inline-flex items-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 px-2.5 py-1.5 rounded-lg text-xs font-bold active:scale-95 transition-all" title="Delete">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const AuditTab = ({ logs }) => (
  <div data-testid="audit-tab-content">
    <div className="mb-4">
      <h2 className="font-extrabold text-slate-900">Audit Log (Last 100)</h2>
      <p className="text-xs text-slate-400">Every create/update/delete action is recorded here</p>
    </div>
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Time (IST)", "User", "Action", "Module", "Entity", "Detail"].map((h) => (
                <th key={h} className="text-left px-3 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400">No audit entries yet</td></tr>
            ) : logs.map((l, i) => (
              <tr key={i} className={`border-t border-slate-100 ${i % 2 === 0 ? "" : "bg-slate-50/40"}`}>
                <td className="px-3 py-3 font-mono text-xs text-slate-500">{l.timestamp}</td>
                <td className="px-3 py-3 font-bold text-[#1A3560]">{l.user_id}</td>
                <td className="px-3 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                    l.action === "CREATE" ? "bg-green-100 text-green-700" :
                    l.action === "UPDATE" ? "bg-blue-100 text-blue-700" :
                    l.action === "DELETE" ? "bg-red-100 text-red-700" :
                    "bg-slate-100 text-slate-700"
                  }`}>{l.action}</span>
                </td>
                <td className="px-3 py-3 text-slate-700 text-xs">{l.module}</td>
                <td className="px-3 py-3 font-mono text-xs text-slate-600">{l.entity_id}</td>
                <td className="px-3 py-3 text-xs text-slate-500 max-w-md truncate" title={l.new_value || l.field_changed}>
                  {l.new_value || l.field_changed || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// ============== SHARED ==============
const INP = "w-full h-11 px-3 rounded-lg border border-slate-300 text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#1A3560] disabled:bg-slate-100 disabled:text-slate-500";

const SheetsTab = ({ status, syncing, onConnect, onSync, onDisconnect, onDownloadExcel }) => {
  const connected = status?.connected;
  const url = status?.spreadsheet_url;
  const lastSync = status?.last_sync_at;
  return (
    <div data-testid="sheets-tab-content" className="space-y-6">
      <div>
        <h2 className="font-extrabold text-slate-900">Data Export</h2>
        <p className="text-xs text-slate-400">Get all your plant data in spreadsheet form — downloadable Excel or live Google Sheets sync</p>
      </div>

      {/* PRIMARY OPTION: Excel Download — no OAuth needed */}
      <div className="bg-white rounded-2xl border-2 border-[#15803D] p-6 max-w-2xl">
        <div className="flex items-start gap-3 mb-4">
          <div className="bg-[#15803D]/10 p-2.5 rounded-xl">
            <Download size={24} className="text-[#15803D]" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-extrabold text-slate-900">Download Excel (.xlsx)</h3>
              <span className="text-[10px] bg-[#15803D] text-white font-bold px-2 py-0.5 rounded-full">RECOMMENDED</span>
            </div>
            <p className="text-xs text-slate-500">
              One workbook with 11 tabs covering RM, Production, Inspections, Audit Log and all Master Data.
              Opens in Excel, Google Sheets (File → Import), or LibreOffice. No login needed.
            </p>
          </div>
        </div>
        <button data-testid="download-excel-btn" onClick={onDownloadExcel}
          className="flex items-center gap-2 bg-[#15803D] text-white px-5 py-3 rounded-xl text-sm font-bold hover:bg-[#0F5D2D] active:scale-[0.98] transition-all">
          <Download size={16} /> Download Excel Now
        </button>
      </div>

      {/* SECONDARY OPTION: Google Sheets live sync */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-4">
          <Sheet size={28} className={connected ? "text-[#15803D]" : "text-slate-400"} />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-extrabold text-slate-900">Google Sheets Live Sync</h3>
              <span className="text-[10px] bg-slate-200 text-slate-700 font-bold px-2 py-0.5 rounded-full">OPTIONAL</span>
            </div>
            <div className={`text-xs font-bold ${connected ? "text-[#15803D]" : "text-slate-500"} mt-0.5`}>
              {connected ? "Connected" : "Not Connected"}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {connected
                ? "Push the latest data to your live Google Sheet on demand."
                : "Auto-sync to a live Google Sheet (requires one-time Google sign-in)."}
            </p>
          </div>
        </div>

        {connected && url && (
          <a data-testid="sheet-link" href={url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-[#1A3560] font-bold hover:underline mb-3">
            <ExternalLink size={14} /> Open Spreadsheet
          </a>
        )}
        {lastSync && (
          <p className="text-xs text-slate-500 mb-3">Last sync: <b>{new Date(lastSync).toLocaleString()}</b></p>
        )}

        <div className="flex flex-wrap gap-2">
          {!connected && (
            <button data-testid="connect-sheets-btn" onClick={onConnect}
              className="flex items-center gap-2 bg-[#1A3560] text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-[#102340] active:scale-[0.98] transition-all">
              <Sheet size={16} /> Connect Google Sheets
            </button>
          )}
          {connected && (
            <>
              <button data-testid="sync-sheets-btn" onClick={onSync} disabled={syncing}
                className="flex items-center gap-2 bg-[#1A3560] text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-[#102340] active:scale-[0.98] transition-all disabled:opacity-50">
                <RefreshCw size={16} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing..." : "Sync Now"}
              </button>
              <button data-testid="disconnect-sheets-btn" onClick={onDisconnect}
                className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-50 active:scale-[0.98] transition-all">
                <XCircle size={16} /> Disconnect
              </button>
            </>
          )}
        </div>
      </div>

      <div className="max-w-2xl bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm font-bold text-blue-800 mb-1">What gets exported?</p>
        <p className="text-xs text-blue-700">RM Receipts • Production Stage 1 & 2 • Inspections • Daily Checks • RM Issues • Audit Log • Master Data (Machines, Tools, Users, Inspection Plan).
        Each module gets its own tab.</p>
      </div>
    </div>
  );
};

const ShortfallsTab = ({ shortfalls, dateFilter, setDateFilter, onResolve, onLookup }) => {
  const open = shortfalls.filter((s) => s.wip_shortfall || s.wip_shortfall_at_start);
  return (
    <div data-testid="shortfalls-tab-content" className="space-y-4">
      <div>
        <h2 className="font-extrabold text-slate-900 flex items-center gap-2">
          <AlertTriangle size={20} className="text-amber-600" /> WIP Shortfalls
        </h2>
        <p className="text-xs text-slate-400">
          Jobs that started on op &gt; 1 without sufficient WIP recorded in the system.
          <b> "Adjust"</b> back-fills the missing parts so the audit chain matches reality;
          <b> "Dismiss"</b> just acknowledges the flag (use when reconciliation will happen outside the system).
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 max-w-3xl">
        <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Date</label>
        <input
          data-testid="shortfall-date-filter"
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="h-10 px-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
        />
        {dateFilter && (
          <button onClick={() => setDateFilter("")} className="text-xs text-slate-500 underline">Clear</button>
        )}
        <span className="ml-auto text-xs text-slate-500">
          <b className="text-amber-700">{open.length}</b> open • <b>{shortfalls.length - open.length}</b> resolved
        </span>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden max-w-5xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Date", "Job", "Op", "Machine", "Part", "Operator", "Avail", "Short", "Status", ""].map((h) => (
                  <th key={h} className="text-left px-3 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shortfalls.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-12 text-slate-400">
                  <CheckCircle2 size={36} className="mx-auto mb-2 opacity-40" />
                  No shortfalls — all jobs have proper WIP coverage.
                </td></tr>
              ) : shortfalls.map((s, i) => {
                const isOpen = s.wip_shortfall || s.wip_shortfall_at_start;
                return (
                  <tr key={s.job_id} className={`border-t border-slate-100 ${i % 2 === 0 ? "" : "bg-slate-50/40"} ${!isOpen ? "opacity-50" : ""}`}>
                    <td className="px-3 py-3 text-xs text-slate-500">{s.date}</td>
                    <td className="px-3 py-3">
                      <button onClick={() => onLookup(s.job_id)} className="font-mono text-xs text-blue-600 hover:underline">
                        {s.job_id}
                      </button>
                    </td>
                    <td className="px-3 py-3 font-bold">{s.operation_seq}/{s.total_operations}</td>
                    <td className="px-3 py-3 text-xs">{s.machine_code}</td>
                    <td className="px-3 py-3 text-xs max-w-[200px] truncate" title={s.part_name}>{s.part_name}</td>
                    <td className="px-3 py-3 text-xs text-slate-500">{s.operator_name}</td>
                    <td className="px-3 py-3 text-center text-xs">{s.wip_available_at_start ?? 0}</td>
                    <td className="px-3 py-3 text-center font-extrabold text-amber-700">{s.wip_shortfall_qty ?? "—"}</td>
                    <td className="px-3 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-bold ${isOpen ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                        {isOpen ? "OPEN" : "RESOLVED"}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      {isOpen && (
                        <button
                          data-testid={`resolve-${s.job_id}`}
                          onClick={() => onResolve(s)}
                          className="bg-[#1A3560] text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-[#102340] active:scale-[0.98]"
                        >
                          Resolve
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const TraceTab = ({ jobId, setJobId, tracing, data, fgData, onLookup,
                    parts, selectedPart, partJobs, onSelectPart,
                    rmMapping, showRmMapping, setShowRmMapping,
                    rmMappingLoading, onAssignRm, onAutoMatch, autoMatching,
                    onRefreshMapping }) => {
  const [partSearch, setPartSearch] = useState("");
  const filteredParts = (parts || []).filter((p) => {
    if (!partSearch.trim()) return true;
    const q = partSearch.toLowerCase();
    return (p.part_name || "").toLowerCase().includes(q) || (p.part_no || "").toLowerCase().includes(q);
  });
  const pendingCount = rmMapping?.total || 0;
  return (
  <div data-testid="trace-tab-content" className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
    {/* LEFT: Browse by part */}
    <aside className="space-y-2">
      <div>
        <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">Browse by Part</p>
        <p className="text-[11px] text-slate-400">{filteredParts.length} part{filteredParts.length === 1 ? "" : "s"} ever produced</p>
      </div>
      <input
        data-testid="trace-part-search"
        type="text"
        value={partSearch}
        onChange={(e) => setPartSearch(e.target.value)}
        placeholder="Search part name / part no…"
        className="w-full h-9 px-3 rounded-lg border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
      />
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden max-h-[70vh] overflow-y-auto">
        {filteredParts.length === 0 ? (
          <p className="text-xs text-slate-400 p-4 text-center">No parts found.</p>
        ) : filteredParts.map((p) => {
          const isSel = selectedPart && (selectedPart.part_no || "") === (p.part_no || "")
                       && (selectedPart.part_name || "") === (p.part_name || "");
          return (
            <button
              key={`${p.part_no}|${p.part_name}`}
              data-testid={`trace-part-${p.part_no || p.part_name}`}
              onClick={() => onSelectPart(p)}
              className={`w-full text-left px-3 py-2.5 border-b border-slate-100 last:border-b-0 transition-all ${isSel ? "bg-[#1A3560]/10" : "hover:bg-slate-50"}`}
            >
              <p className={`text-xs font-bold truncate ${isSel ? "text-[#1A3560]" : "text-slate-800"}`} title={p.part_name}>{p.part_name || "(unnamed)"}</p>
              <div className="flex items-center justify-between mt-0.5">
                <p className="text-[10px] font-mono text-slate-400 truncate">{p.part_no || "—"}</p>
                <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{p.job_count}</span>
              </div>
              {p.last_run && <p className="text-[10px] text-slate-400 mt-0.5">last run {p.last_run}</p>}
            </button>
          );
        })}
      </div>
    </aside>

    {/* RIGHT: Search-based lookup OR part jobs */}
    <div data-testid="trace-right-pane" className="space-y-4">
      <div>
        <h2 className="font-extrabold text-slate-900">Universal Traceability</h2>
        <p className="text-xs text-slate-400">Look up by <b>Job ID</b>, <b>FG Batch Number</b>, or <b>Invoice Number</b>. FG batch numbers like <code className="bg-slate-100 px-1 rounded text-[10px]">B-2026-001-L1-260507-L1-260509</code> are auto-detected. Or pick a part from the left to see every job that produced it.</p>
      </div>

      {/* RM Batch Mapping pill — shown when manual-batch jobs exist */}
      {pendingCount > 0 && (
        <button
          data-testid="rm-mapping-toggle"
          onClick={() => setShowRmMapping(!showRmMapping)}
          className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-2xl border-2 transition-all ${
            showRmMapping
              ? "bg-amber-50 border-amber-300 text-amber-900"
              : "bg-white border-amber-200 hover:bg-amber-50 text-slate-800"
          }`}
        >
          <div className="flex items-center gap-2 text-left">
            <div className="bg-amber-100 text-amber-700 w-9 h-9 rounded-xl flex items-center justify-center font-extrabold text-sm">
              {pendingCount}
            </div>
            <div>
              <p className="font-extrabold text-sm">RM Batch Mapping</p>
              <p className="text-[11px] text-slate-500">
                {rmMapping?.pending_no_batch || 0} pending-no-batch · {rmMapping?.manually_typed || 0} manually-typed · {rmMapping?.auto_matchable || 0} auto-matchable
              </p>
            </div>
          </div>
          <span className="text-[11px] font-bold text-slate-500">{showRmMapping ? "Hide ▴" : "Open ▾"}</span>
        </button>
      )}

      {showRmMapping && (
        <RmMappingView mapping={rmMapping} loading={rmMappingLoading}
          onAssign={onAssignRm} onAutoMatch={onAutoMatch}
          autoMatching={autoMatching} onRefresh={onRefreshMapping} />
      )}

      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="flex gap-2 flex-wrap">
          <input
            data-testid="trace-job-input"
            type="text"
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onLookup(jobId.trim())}
            placeholder="J20260507-004  •  B-2026-001-L1-260507-L1-260509  •  INV-2026-001"
            className="flex-1 min-w-[260px] h-11 px-3 rounded-lg border border-slate-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
          />
          <button
            data-testid="trace-lookup-btn"
            onClick={() => onLookup(jobId.trim())}
            disabled={tracing || !jobId.trim()}
            className="bg-[#1A3560] text-white px-5 py-2 rounded-xl text-sm font-bold disabled:opacity-50 active:scale-[0.98]"
          >
            {tracing ? "Looking up…" : "Trace"}
          </button>
        </div>
      </div>

      {/* Part → jobs list */}
      {selectedPart && !data && !fgData && (
        <TracePartJobsView part={selectedPart} jobs={partJobs} onLookupJob={(jid) => { setJobId(jid); onLookup(jid); }} />
      )}

      {fgData && <FgTraceResults data={fgData} />}

    {data && (
      <div className="space-y-4 max-w-3xl">
        {/* Job header */}
        <div className="bg-[#1A3560] text-white rounded-2xl p-4">
          <p className="text-white/60 text-xs uppercase tracking-widest font-bold">Job ID</p>
          <p className="font-extrabold text-2xl">{data.job.job_id}</p>
          <div className="grid grid-cols-3 gap-3 mt-3 text-sm">
            <div><p className="text-white/50 text-xs">Operation</p><p className="font-bold">{data.job.operation_seq || "?"} of {data.job.total_operations || "?"}</p></div>
            <div><p className="text-white/50 text-xs">Machine</p><p className="font-bold">{data.job.machine_code}</p></div>
            <div><p className="text-white/50 text-xs">Operator</p><p className="font-bold text-xs">{data.job.operator_name}</p></div>
            <div><p className="text-white/50 text-xs">Tool</p><p className="font-bold">T{data.job.tool_no}</p></div>
            <div><p className="text-white/50 text-xs">Date</p><p className="font-bold">{data.job.date}</p></div>
            <div><p className="text-white/50 text-xs">Status</p><p className="font-bold">{data.job.status}</p></div>
          </div>
        </div>

        {/* RM source */}
        {data.rm_receipt && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4">
            <p className="text-xs font-extrabold uppercase tracking-widest text-amber-800 mb-2">📦 Source RM Batch</p>
            <p className="font-extrabold text-lg text-amber-900">{data.rm_receipt.batch_code}</p>
            <p className="text-xs text-amber-800 mt-1">
              {data.rm_receipt.rm_grade || data.rm_receipt.grade} {data.rm_receipt.thickness_mm}mm × {data.rm_receipt.width_mm}mm
              &nbsp;|&nbsp; Total: {data.rm_receipt.quantity_kg} kg
              {data.job.rm_consumed_kg !== undefined && <> &nbsp;|&nbsp; <b>RM consumed at this op: {data.job.rm_consumed_kg} kg</b></>}
            </p>
          </div>
        )}

        {/* Op chain */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500 mb-3">🔗 Operation Chain — Part {data.job.part_no}</p>
          {(data.siblings || []).length === 0 ? (
            <p className="text-sm text-slate-400">No sibling operations found.</p>
          ) : (
            <div className="relative">
              {(data.siblings || []).map((s, i, arr) => (
                <div key={s.job_id} className="flex items-start gap-3 pb-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center font-extrabold text-sm ${s.job_id === data.job.job_id ? "bg-[#1A3560] text-white" : "bg-slate-100 text-slate-600"}`}>
                      {s.operation_seq || "?"}
                    </div>
                    {i < arr.length - 1 && <div className="w-0.5 h-8 bg-slate-200 mt-1" />}
                  </div>
                  <div className="flex-1 pt-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs font-mono text-slate-500">{s.job_id}{s.job_id === data.job.job_id && <span className="ml-2 bg-[#1A3560] text-white text-[10px] px-1.5 py-0.5 rounded font-bold">YOU ARE HERE</span>}</p>
                        <p className="text-sm font-bold text-slate-900">T{s.tool_no} • {s.machine_code}</p>
                        <p className="text-xs text-slate-500">{s.operator_name} • {s.date}</p>
                      </div>
                      <div className="text-right text-xs">
                        <p className="text-[#15803D] font-extrabold">OK: {s.ok_qty ?? "—"}</p>
                        {(s.ng_qty || 0) > 0 && <p className="text-[#DC2626]">NG: {s.ng_qty}</p>}
                        <p className="text-slate-400">{s.status}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* WIP/FG state */}
        {(data.wip_rows || []).length > 0 && (
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-4">
            <p className="text-xs font-extrabold uppercase tracking-widest text-emerald-800 mb-3">📊 Current Inventory for this Part</p>
            <div className="space-y-2">
              {data.wip_rows.map((w) => (
                <div key={w._key} className="flex justify-between items-center bg-white rounded-lg px-3 py-2">
                  <div>
                    <p className="font-bold text-sm text-slate-900">
                      {w.is_fg ? "🏆 FG (Finished Goods)" : `Op ${w.op_seq} WIP`}
                    </p>
                    <p className="text-xs text-slate-500">In: {w.qty_in_total || 0} | Out: {w.qty_out_total || 0}</p>
                  </div>
                  <p className={`font-extrabold text-lg ${w.is_fg ? "text-emerald-700" : "text-amber-700"}`}>{w.qty_available} pcs</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )}

    {!data && !tracing && !selectedPart && (
      <div className="max-w-2xl bg-blue-50 border border-blue-200 rounded-xl p-4 text-xs text-blue-800">
        <p className="font-bold mb-1">💡 Tip</p>
        <p>Pick any Job ID from the Audit Log or Live Dashboard, or click a part on the left to see every job that produced it. The trace shows the source RM batch, every operation in the chain (with OK/NG counts), and the current standing WIP / FG inventory for that part.</p>
      </div>
    )}
    </div>
  </div>
  );
};

const TracePartJobsView = ({ part, jobs, onLookupJob }) => (
  <div data-testid="trace-part-jobs" className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
      <div>
        <p className="font-extrabold text-slate-900 text-sm">{part.part_name || "(unnamed)"}</p>
        <p className="text-[11px] font-mono text-slate-500">{part.part_no || "—"}</p>
      </div>
      <span className="text-xs bg-slate-200 text-slate-700 px-3 py-1 rounded-full font-bold">
        {jobs == null ? "Loading…" : `${jobs.length} job${jobs.length === 1 ? "" : "s"}`}
      </span>
    </div>
    {jobs == null ? (
      <div className="text-center py-12 text-slate-400 text-sm">Loading jobs…</div>
    ) : jobs.length === 0 ? (
      <div className="text-center py-12 text-slate-400 text-sm">No jobs found for this part.</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              {["Job ID", "Date", "Op", "Machine", "Tool", "Operator", "RM Batch", "OK", "NG", "FG Batch", "Status", ""].map((h) => (
                <th key={h} className="text-left px-3 py-2.5 font-bold uppercase tracking-widest text-[10px] text-slate-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.job_id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono font-bold text-[#1A3560]">{j.job_id}</td>
                <td className="px-3 py-2 text-slate-600">{j.date}</td>
                <td className="px-3 py-2 text-slate-600">{j.operation_seq || "?"}/{j.total_operations || "?"}</td>
                <td className="px-3 py-2">{j.machine || "—"}</td>
                <td className="px-3 py-2">T{j.tool_no}</td>
                <td className="px-3 py-2">{j.operator || "—"}</td>
                <td className="px-3 py-2 font-mono text-slate-600">{j.rm_batch || "—"}</td>
                <td className="px-3 py-2 font-bold text-[#15803D]">{j.ok_qty || 0}</td>
                <td className="px-3 py-2 text-[#DC2626]">{j.ng_qty || 0}</td>
                <td className="px-3 py-2 font-mono text-[10px]">
                  {(j.fg_batches || []).length === 0 ? "—" :
                    <div className="space-y-0.5">
                      {j.fg_batches.map((fg) => (
                        <button key={fg} onClick={() => onLookupJob(fg)} className="block text-violet-700 hover:underline truncate max-w-[180px]" title={fg}>{fg}</button>
                      ))}
                    </div>
                  }
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${j.status === "COMPLETE" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{j.status}</span>
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => onLookupJob(j.job_id)} className="text-[10px] font-bold text-[#1A3560] hover:underline">Trace →</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const RmMappingView = ({ mapping, loading, onAssign, onAutoMatch, autoMatching, onRefresh }) => {
  const jobs = mapping?.jobs || [];
  return (
    <div data-testid="rm-mapping-view" className="bg-white rounded-2xl border border-amber-200 overflow-hidden">
      <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="font-extrabold text-amber-900 text-sm">RM Batch Mapping</p>
          <p className="text-[11px] text-amber-700">
            Jobs that started without a linked RM batch ("Batch not available"), or were imported with a manually-typed batch code. Assign the real RM batch to back-fill stock + lineage.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="rm-auto-match-btn"
            onClick={onAutoMatch}
            disabled={autoMatching || (mapping?.auto_matchable || 0) === 0}
            className="bg-[#15803D] hover:bg-[#0F5D2D] text-white px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50"
            title="Best fuzzy match to your manual text — or, if no text was typed, oldest FIFO RM receipt with matching spec"
          >
            {autoMatching ? "Auto-matching…" : `Auto-match all ${mapping?.auto_matchable || 0} matchable`}
          </button>
          <button onClick={onRefresh} className="bg-white border border-slate-300 px-3 py-1.5 rounded-lg text-xs font-bold">↻</button>
        </div>
      </div>
      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">No jobs need mapping. 🎉</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Job", "Date", "Part", "Spec", "Original Batch", "Status", "Candidates", ""].map((h) => (
                  <th key={h} className="text-left px-3 py-2.5 font-bold uppercase tracking-widest text-[10px] text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => <RmMappingRow key={j.job_id} job={j} onAssign={onAssign} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const RmMappingRow = ({ job, onAssign }) => {
  const [picked, setPicked] = useState(job.candidates?.[0]?.batch_code || "");
  const [sub, setSub] = useState(job.sub_code || "A");
  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="px-3 py-3 font-mono font-bold text-[#1A3560]">{job.job_id}</td>
      <td className="px-3 py-3 text-slate-600">{job.date}</td>
      <td className="px-3 py-3">
        <p className="font-bold text-slate-900 text-[11px] max-w-[200px] truncate" title={job.part_name}>{job.part_name || "—"}</p>
        <p className="text-[10px] font-mono text-slate-400">{job.part_no || ""}</p>
        <p className="text-[10px] text-slate-500">T{job.tool_no} · {job.machine_code}</p>
      </td>
      <td className="px-3 py-3 text-[11px] text-slate-600">
        {job.rm_grade || "?"}<br />
        <span className="text-slate-400">{job.thickness ?? "?"}mm × {job.width ?? "?"}mm</span>
      </td>
      <td className="px-3 py-3 font-mono text-[11px] text-amber-700">{job.original_batch || <span className="italic text-slate-400">(empty)</span>}</td>
      <td className="px-3 py-3 text-[10px]">
        {job.pending_batch_assignment ? (
          <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">PENDING (no batch)</span>
        ) : (
          <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">MANUALLY TYPED</span>
        )}
        {job.stage2_done && <span className="block mt-1 text-slate-500">S2 done · {job.stage2_ok_qty || 0} OK</span>}
      </td>
      <td className="px-3 py-3">
        {job.candidates.length === 0 ? (
          <span className="text-[11px] text-slate-400 italic">No candidates — verify spec on tool master</span>
        ) : (
          <select
            data-testid={`rm-pick-${job.job_id}`}
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            className="text-[11px] border border-slate-300 rounded px-2 py-1 font-mono w-full max-w-[260px]"
          >
            {job.candidates.map((c) => (
              <option key={c.batch_code} value={c.batch_code}>
                {c.batch_code} · {c.supplier || "?"} · {c.date} · rem {c.remaining_kg}kg
              </option>
            ))}
          </select>
        )}
      </td>
      <td className="px-3 py-3">
        {job.candidates.length > 0 && (
          <div className="flex items-center gap-1">
            <select value={sub} onChange={(e) => setSub(e.target.value)} className="text-[11px] border border-slate-300 rounded px-1 py-1">
              {["A", "B", "C", "D"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button
              data-testid={`rm-apply-${job.job_id}`}
              onClick={() => picked && onAssign(job.job_id, picked, sub)}
              disabled={!picked}
              className="bg-[#1A3560] hover:bg-[#102340] text-white px-2.5 py-1 rounded-lg text-[10px] font-bold disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};


const ReportsTab = ({ status, sending, onSendNow, onPreviewPdf, onRefresh,
                      hourlyStatus, hourlyPreview, sendingHourly, onSendHourlyNow }) => {
  if (!status) return <div className="text-center py-12 text-slate-400">Loading…</div>;
  const recipients = status.recipients || [];
  const runs = status.recent_runs || [];
  const ok = status.resend_configured;
  return (
    <div data-testid="reports-tab-content" className="space-y-6">
      <div>
        <h2 className="font-extrabold text-slate-900">Daily Shift-End Reports</h2>
        <p className="text-xs text-slate-400">Automatic PDF + Excel snapshot emailed every day at the configured time.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl">
        <div className="flex items-start gap-3">
          <div className={`p-2.5 rounded-xl ${ok ? "bg-[#15803D]/10" : "bg-red-50"}`}>
            <Mail size={24} className={ok ? "text-[#15803D]" : "text-red-600"} />
          </div>
          <div className="flex-1">
            <h3 className="font-extrabold text-slate-900">
              {ok ? "Email Delivery: Configured" : "Email Delivery: NOT Configured"}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {ok
                ? "Resend API key is set. Reports will be sent automatically."
                : "RESEND_API_KEY missing in backend .env — reports cannot be sent."}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5 text-sm">
          <div>
            <p className="text-xs uppercase font-bold text-slate-400 tracking-widest mb-1">Schedule (IST)</p>
            <p className="font-extrabold text-slate-900">{status.scheduled_time_ist}</p>
          </div>
          <div>
            <p className="text-xs uppercase font-bold text-slate-400 tracking-widest mb-1">Sender</p>
            <p className="font-mono text-xs text-slate-700">{status.sender || "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase font-bold text-slate-400 tracking-widest mb-1">Recipients ({recipients.length})</p>
            <p className="font-mono text-xs text-slate-700 break-all">{recipients.join(", ") || "—"}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-5">
          <button data-testid="send-report-btn" onClick={onSendNow} disabled={sending || !ok}
            className="flex items-center gap-2 bg-[#15803D] text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-[#0F5D2D] active:scale-[0.98] transition-all disabled:opacity-50">
            <Send size={16} className={sending ? "animate-pulse" : ""} /> {sending ? "Sending…" : "Send Today's Report Now"}
          </button>
          <button data-testid="preview-pdf-btn" onClick={onPreviewPdf}
            className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-50 active:scale-[0.98] transition-all">
            <FileText size={16} /> Preview PDF (no email)
          </button>
          <button onClick={onRefresh}
            className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 px-3 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-50">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <div className="max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Recent Sends ({runs.length})</p>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Date", "Sent At (IST)", "Status", "Recipients", "Trigger", "Email ID"].map((h) => (
                    <th key={h} className="text-left px-3 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-400">No reports sent yet — click <b>Send Today's Report Now</b> above.</td></tr>
                ) : runs.map((r, i) => (
                  <tr key={i} className={`border-t border-slate-100 ${i % 2 === 0 ? "" : "bg-slate-50/40"}`}>
                    <td className="px-3 py-3 font-bold">{r.date}</td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-500">{r.sent_at}</td>
                    <td className="px-3 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-bold ${r.success ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {r.success ? "Sent" : "Failed"}
                      </span>
                      {!r.success && r.error && <p className="text-[11px] text-red-600 mt-1">{r.error}</p>}
                    </td>
                    <td className="px-3 py-3 text-xs">{(r.recipients || []).join(", ")}</td>
                    <td className="px-3 py-3 text-xs text-slate-500">{r.triggered_by}</td>
                    <td className="px-3 py-3 font-mono text-[10px] text-slate-400">{r.email_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="max-w-2xl bg-blue-50 border border-blue-200 rounded-xl p-4 text-xs text-blue-800">
        <p className="font-bold mb-1">What's in the daily email?</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li><b>PDF:</b> One-page summary — Total OK/NG, NG%, downtime, machine-wise output, inspections (pass/fail), checksheet status.</li>
          <li><b>Excel:</b> 11-tab workbook with full record-level data for the entire database.</li>
        </ul>
      </div>

      <HourlyInspectionPanel
        status={hourlyStatus}
        preview={hourlyPreview}
        sending={sendingHourly}
        onSendNow={onSendHourlyNow}
        onRefresh={onRefresh}
      />
    </div>
  );
};

const HourlyInspectionPanel = ({ status, preview, sending, onSendNow, onRefresh }) => {
  if (!status) return null;
  const recipients = status.recipients || [];
  const runs = status.recent_runs || [];
  const rows = preview?.rows || [];
  const counts = preview?.counts || { OVERDUE: 0, DUE: 0, "ON-TIME": 0 };
  const ok = status.resend_configured && recipients.length > 0;
  const stColour = (s) =>
    s === "OVERDUE" ? "bg-red-100 text-red-700"
    : s === "DUE" ? "bg-amber-100 text-amber-700"
    : "bg-emerald-100 text-emerald-700";

  return (
    <div data-testid="hourly-inspection-panel" className="space-y-4 pt-4 border-t-2 border-dashed border-slate-200">
      <div>
        <h2 className="font-extrabold text-slate-900">Hourly Inspection-Frequency Email</h2>
        <p className="text-xs text-slate-400">
          Fires every hour from {status.start_hour_ist}:00 to {status.end_hour_ist}:00 IST.
          Skipped automatically if no jobs are running.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl">
        <div className="flex items-start gap-3">
          <div className={`p-2.5 rounded-xl ${ok ? "bg-[#15803D]/10" : "bg-amber-50"}`}>
            <Mail size={24} className={ok ? "text-[#15803D]" : "text-amber-600"} />
          </div>
          <div className="flex-1">
            <h3 className="font-extrabold text-slate-900">
              {ok ? "Hourly Email: Configured" : "Hourly Email: Recipients Missing"}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {ok
                ? "Scheduled hourly between the configured hours. Click Send Now to fire a test."
                : "HOURLY_REPORT_RECIPIENTS missing in backend .env."}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5 text-sm">
          <div>
            <p className="text-xs uppercase font-bold text-slate-400 tracking-widest mb-1">Window (IST)</p>
            <p className="font-extrabold text-slate-900">{status.start_hour_ist}:00 – {status.end_hour_ist}:00</p>
          </div>
          <div>
            <p className="text-xs uppercase font-bold text-slate-400 tracking-widest mb-1">Sender</p>
            <p className="font-mono text-xs text-slate-700">{status.sender || "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase font-bold text-slate-400 tracking-widest mb-1">Recipients ({recipients.length})</p>
            <p className="font-mono text-xs text-slate-700 break-all">{recipients.join(", ") || "—"}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-5">
          <button data-testid="send-hourly-btn" onClick={onSendNow} disabled={sending || !ok}
            className="flex items-center gap-2 bg-[#15803D] text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-[#0F5D2D] active:scale-[0.98] transition-all disabled:opacity-50">
            <Send size={16} className={sending ? "animate-pulse" : ""} /> {sending ? "Sending…" : "Send Hourly Email Now"}
          </button>
          <button onClick={onRefresh}
            className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 px-3 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-50">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Live preview of what the next email will contain */}
      <div className="max-w-3xl">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
            Live Preview · {rows.length} running job{rows.length === 1 ? "" : "s"}
          </p>
          <div className="flex gap-1.5 text-[11px] font-bold">
            <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{counts.OVERDUE || 0} OVERDUE</span>
            <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{counts.DUE || 0} DUE</span>
            <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{counts["ON-TIME"] || 0} ON-TIME</span>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Machine", "Tool", "Part", "Operator", "Freq.", "Last Inspection", "Status"].map((h) => (
                    <th key={h} className="text-left px-3 py-2.5 font-bold uppercase tracking-widest text-[10px] text-slate-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400">No jobs running right now — the hourly email is auto-skipped.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.job_id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-bold text-[#1A3560]">{r.machine || "—"}</td>
                    <td className="px-3 py-2">T{r.tool_no}</td>
                    <td className="px-3 py-2">
                      <p className="font-bold text-slate-800 truncate max-w-[200px]" title={r.part_name}>{r.part_name || "—"}</p>
                      <p className="text-[10px] font-mono text-slate-400">{r.part_no || ""}</p>
                    </td>
                    <td className="px-3 py-2">{r.operator || "—"}</td>
                    <td className="px-3 py-2">every {r.interval_hours} hrs</td>
                    <td className="px-3 py-2 text-slate-600">
                      {r.mins_since_last == null ? "Never"
                        : r.mins_since_last < 120 ? `${Math.round(r.mins_since_last)} min ago`
                        : `${(r.mins_since_last / 60).toFixed(1)} hrs ago`}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-2 py-1 rounded-full font-bold ${stColour(r.status)}`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Recent sends */}
      <div className="max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Recent Hourly Sends ({runs.length})</p>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Sent At (IST)", "Slot", "Status", "Jobs", "Trigger"].map((h) => (
                    <th key={h} className="text-left px-3 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-6 text-slate-400">No hourly emails sent yet.</td></tr>
                ) : runs.map((r, i) => (
                  <tr key={r.sent_at || i} className={`border-t border-slate-100 ${i % 2 === 0 ? "" : "bg-slate-50/40"}`}>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{r.sent_at}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{r.hour_slot || "—"}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-xs px-2 py-1 rounded-full font-bold ${r.success ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {r.success ? "Sent" : "Failed"}
                      </span>
                      {!r.success && r.error && <p className="text-[10px] text-red-600 mt-1 max-w-[260px] truncate" title={r.error}>{r.error}</p>}
                    </td>
                    <td className="px-3 py-2.5 font-bold">{r.job_count || 0}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{r.triggered_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const IMPORT_KINDS = [
  { kind: "machines", label: "Machines Master", note: "Sheet 'Machines'. Machine Code must start with 'AMPL-'. Updates `machines` (upsert).", filename: "machines.xlsx", has_template: true },
  { kind: "parts", label: "Parts Master", note: "Sheet '20250530'. Internal Part No must start with 'AMPL-'. Updates `parts` (upsert).", filename: "parts.xlsx", has_template: true },
  { kind: "tools", label: "Tools Master", note: "Sheet '20250530', 11 columns. ⚠ FULL REPLACE — wipes existing tools, then imports.", filename: "tools.xlsx", has_template: true },
  { kind: "rm", label: "RM Receipts + Issue Register", note: "Two sheets: 'Raw Material Logs' + 'RM Issue Register'. ⚠ FULL REPLACE — wipes existing receipts AND issues, then imports. Batch Code from file is preserved.", filename: "rm.xlsx", has_template: true },
  { kind: "production", label: "Production Logs", note: "Sheet 'Production Log Book'. ⚠ FULL REPLACE — wipes existing Stage-1+Stage-2, then imports. Job ID and Batch Code from file are preserved.", filename: "production.xlsx", has_template: true },
  { kind: "inspection_plan", label: "Inspection Plans (QC Parameters)", note: "Sheet 'Inspection Plan'. ⚠ FULL REPLACE — wipes existing inspection plan rows, then imports. Cavity No supports per-cavity tolerances.", filename: "inspection_plan.xlsx", has_template: true },
];

const ImportRow = ({ k, importing, onUpload, onDownloadTemplate }) => {
  const [file, setFile] = useState(null);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 max-w-3xl">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="bg-[#1A3560]/10 p-2.5 rounded-xl">
          <FileSpreadsheet size={22} className="text-[#1A3560]" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="font-extrabold text-slate-900">{k.label}</p>
          <p className="text-xs text-slate-500 mt-0.5">{k.note}</p>
          <p className="text-[11px] text-slate-400 mt-1">Expected file extension: <code className="bg-slate-100 px-1.5 py-0.5 rounded">.{k.filename.split('.').pop()}</code></p>
        </div>
        {k.has_template && (
          <button
            data-testid={`import-template-${k.kind}`}
            onClick={() => onDownloadTemplate(k.kind)}
            className="flex items-center gap-1.5 bg-white border border-[#1A3560] text-[#1A3560] px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-[#1A3560]/5 active:scale-[0.98] transition-all"
            title="Download a blank Excel template"
          >
            <Download size={12} /> Template
          </button>
        )}
      </div>
      <div className="flex gap-2 mt-3 flex-wrap">
        <input
          type="file"
          accept=".xlsx,.xlsm"
          data-testid={`import-file-${k.kind}`}
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="block text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-slate-100 file:text-[#1A3560] hover:file:bg-slate-200"
        />
        <button
          data-testid={`import-upload-${k.kind}`}
          onClick={() => onUpload(k.kind, file)}
          disabled={!file || importing}
          className="flex items-center gap-2 bg-[#1A3560] text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-[#102340] active:scale-[0.98] transition-all disabled:opacity-40"
        >
          <Upload size={14} /> {importing ? "Uploading…" : "Import"}
        </button>
      </div>
    </div>
  );
};

const ImportTab = ({ importing, status, onUpload, onDownloadTemplate,
                     onSyncHistorical, syncing, syncResult }) => (
  <div data-testid="import-tab-content" className="space-y-4">
    <div>
      <h2 className="font-extrabold text-slate-900">Historical Data Import</h2>
      <p className="text-xs text-slate-400">Upload past Excel registers — Machines & Parts <b>upsert</b> (existing rows update, new rows insert). Tools, RM, Production, and Inspection Plans are <b>full replace</b> (wipe + re-import).</p>
    </div>

    {/* Sync-historical-data — surfaces master-data changes into old records */}
    <div className="max-w-3xl bg-blue-50 border border-blue-200 rounded-2xl p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-blue-700 flex-shrink-0">
          <RefreshCw size={20} className={syncing ? "animate-spin" : ""} />
        </div>
        <div className="flex-1">
          <p className="font-extrabold text-slate-900 text-sm">Sync historical data from current masters</p>
          <p className="text-[11px] text-slate-600 mt-0.5">
            Fixes old records that still show renamed parts, edited tool details, or old operator names. Pulls current values from Users, Tools, Parts and RM Receipts and pushes them onto every historical production, inspection, FG batch, WIP lot, and visual-inspection row. Idempotent — safe to click any time.
          </p>
        </div>
        <button
          data-testid="sync-historical-btn"
          onClick={onSyncHistorical}
          disabled={syncing}
          className="bg-[#1A3560] hover:bg-[#102340] text-white px-4 py-2 rounded-xl text-xs font-bold disabled:opacity-50 flex-shrink-0"
        >
          {syncing ? "Syncing…" : "Sync now"}
        </button>
      </div>
      {syncResult && (
        <div className="mt-3 bg-white rounded-xl p-3 text-[11px] text-slate-700 space-y-1.5">
          <p className="font-bold text-emerald-700">
            ✓ {syncResult.total_docs_updated} record(s) updated
          </p>
          {(() => {
            const merged = {};
            for (const src of ["from_tools", "from_users", "from_rm_receipts"]) {
              const d = syncResult[src]?.docs_updated || {};
              for (const [coll, n] of Object.entries(d)) merged[coll] = (merged[coll] || 0) + n;
            }
            const entries = Object.entries(merged);
            if (entries.length === 0) return <p className="text-slate-500 italic">Nothing to change — all history was already in sync.</p>;
            return (
              <ul className="list-disc list-inside space-y-0.5">
                {entries.map(([c, v]) => <li key={c}>{c}: <b>{v}</b></li>)}
              </ul>
            );
          })()}
        </div>
      )}
    </div>

    {status && (
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 max-w-3xl text-sm">
        <p className="font-bold text-green-800">Import complete: {status.kind} ({status.filename})</p>
        <p className="text-xs text-green-700 mt-1">
          Rows added: {Object.entries(status.added).map(([c, v]) => `${c} ${v >= 0 ? "+" : ""}${v}`).join("  •  ")}
        </p>
      </div>
    )}

    <div className="space-y-3">
      {IMPORT_KINDS.map((k) => (
        <ImportRow key={k.kind} k={k} importing={importing} onUpload={onUpload} onDownloadTemplate={onDownloadTemplate} />
      ))}
    </div>

    <div className="max-w-3xl bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800">
      <p className="font-bold mb-1">⚠ Heads up</p>
      <ul className="list-disc list-inside space-y-0.5">
        <li>Imports are <b>idempotent</b> — re-uploading the same file is safe (rows update by their natural key).</li>
        <li>Production import auto-creates inactive operator accounts for any new names found.</li>
        <li>Large files (10k+ rows) may take 30-60 seconds.</li>
      </ul>
    </div>
  </div>
);

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
    <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
      <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white z-10">
        <h3 className="font-extrabold text-slate-900">{title}</h3>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
          <XCircle size={20} />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const ModalField = ({ label, children }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
    {children}
  </div>
);

// ============== FG TRACEABILITY DISPLAY ==============

const FgTraceResults = ({ data }) => {
  const results = data.results || [];
  const lookup = data.lookup || {};
  return (
    <div className="space-y-4 max-w-4xl" data-testid="fg-trace-results">
      <div className="bg-[#15803D] text-white rounded-2xl p-4">
        <p className="text-white/70 text-xs uppercase tracking-widest font-bold">
          {lookup.invoice_no ? "Invoice Lookup" : "FG Batch Lookup"}
        </p>
        <p className="font-extrabold text-2xl font-mono">{lookup.invoice_no || lookup.fg_batch_no}</p>
        <p className="text-white/80 text-sm mt-1">{results.length} FG batch{results.length === 1 ? "" : "es"} matched</p>
      </div>
      {results.map((fg) => (
        <div key={fg.fg_batch_no} className="bg-white rounded-2xl border-2 border-emerald-200 p-4">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-widest font-bold">FG Batch</p>
              <p className="font-extrabold text-lg text-slate-900 font-mono break-all">{fg.fg_batch_no}</p>
              <p className="text-xs text-slate-500 mt-1">{fg.part_name} ({fg.part_no}) — {fg.qty} pcs</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${fg.status === "DISPATCHED" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                {fg.status}
              </span>
              {fg.invoice_no && (
                <span className="text-[10px] text-slate-500 font-mono">📑 {fg.invoice_no}</span>
              )}
            </div>
          </div>

          {/* Lineage header */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs bg-slate-50 rounded-lg p-3 mb-3">
            <div><p className="text-slate-400 uppercase text-[10px] font-bold">RM Batch</p><p className="font-mono font-bold">{fg.rm_batch_code}</p></div>
            <div><p className="text-slate-400 uppercase text-[10px] font-bold">First Op</p><p className="font-bold">{fg.first_op_loading} · {fg.first_op_close_date}</p></div>
            <div><p className="text-slate-400 uppercase text-[10px] font-bold">Last Op</p><p className="font-bold">{fg.last_op_loading} · {fg.last_op_close_date}</p></div>
            <div><p className="text-slate-400 uppercase text-[10px] font-bold">Qty</p><p className="font-bold text-[#15803D]">{fg.qty} pcs</p></div>
          </div>

          {/* Operation chain */}
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">Full Process Chain</p>
          <div className="space-y-1.5">
            {(fg.history || []).map((h, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-2.5">
                <span className="bg-[#1A3560] text-white text-xs font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">
                  Op{h.op_seq}
                </span>
                <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                  <span><span className="text-slate-400">Job:</span> <b className="font-mono">{h.job_id}</b></span>
                  <span><span className="text-slate-400">Machine:</span> <b>{h.machine}</b></span>
                  <span><span className="text-slate-400">Tool:</span> <b>T{h.tool_no}</b></span>
                  <span><span className="text-slate-400">Operator:</span> <b>{h.operator}</b></span>
                  <span><span className="text-slate-400">Qty:</span> <b className="text-emerald-700">{h.qty}</b> on {h.date}</span>
                </div>
              </div>
            ))}
            <div className="flex items-center gap-3 bg-emerald-50 border-2 border-emerald-300 rounded-lg p-2.5">
              <span className="bg-[#15803D] text-white text-xs font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">
                FG
              </span>
              <p className="text-xs">
                <b className="text-emerald-900">{fg.qty} pcs Finished Goods</b>
                <span className="text-slate-500 ml-2">→ ready for dispatch</span>
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============== FG BATCHES TAB ==============

const FgBatchesTab = ({ batches, onLinkInvoice, onUnlinkInvoice, onTrace }) => {
  const [filter, setFilter] = useState("ALL"); // ALL | READY | DISPATCHED
  const [search, setSearch] = useState("");
  const filtered = batches.filter((b) => {
    if (filter !== "ALL" && b.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!`${b.fg_batch_no} ${b.part_no} ${b.part_name} ${b.rm_batch_code} ${b.invoice_no || ""}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const ready = batches.filter((b) => b.status === "READY").length;
  const dispatched = batches.filter((b) => b.status === "DISPATCHED").length;
  const totalQty = filtered.reduce((s, b) => s + (b.qty || 0), 0);

  return (
    <div data-testid="fg-tab-content">
      <div className="flex justify-between items-start gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-extrabold text-slate-900">FG Batches (Ready for Dispatch)</h2>
          <p className="text-xs text-slate-400">Auto-generated from the lineage chain when the last operation closes. Link an invoice number once the goods are shipped. <b>Print QR labels</b> to stick on cartons — scanning shows full trace.</p>
        </div>
        <div className="flex gap-2 items-center">
          <span className="text-xs bg-emerald-100 text-emerald-700 px-3 py-2 rounded-lg font-bold">{ready} READY</span>
          <span className="text-xs bg-blue-100 text-blue-700 px-3 py-2 rounded-lg font-bold">{dispatched} DISPATCHED</span>
          <button
            data-testid="print-all-labels-btn"
            onClick={async () => {
              try {
                const res = await api.get("/fg-labels.pdf?status=READY", { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                window.open(url, "_blank", "noopener,noreferrer");
                setTimeout(() => window.URL.revokeObjectURL(url), 60000);
              } catch (e) {
                alert(e?.response?.status === 404 ? "No READY batches to print." : "Failed to generate labels PDF.");
              }
            }}
            className="inline-flex items-center gap-1.5 bg-[#1A3560] text-white px-3 py-2 rounded-lg text-xs font-bold hover:bg-[#102340] active:scale-95 transition-all"
            title="Print all READY labels as 8-per-page A4 PDF"
          >
            <Printer size={12} /> Print READY labels
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-4">
        <input
          data-testid="fg-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by FG batch / part / RM / invoice…"
          className="h-10 px-3 rounded-lg border border-slate-300 text-sm flex-1 min-w-[220px] focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
        />
        {["ALL", "READY", "DISPATCHED"].map((s) => (
          <button
            key={s}
            data-testid={`fg-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-3 py-2 text-xs font-bold rounded-lg border transition-all ${
              filter === s ? "bg-[#1A3560] text-white border-[#1A3560]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {s}
          </button>
        ))}
        <span className="text-xs bg-slate-100 text-slate-700 px-3 py-2 rounded-lg font-bold">
          {filtered.length} batches · {totalQty.toLocaleString()} pcs
        </span>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["FG Batch No", "Part", "RM Source", "Qty", "Status", "Invoice", "Actions"].map((h) => (
                  <th key={h} className="text-left px-3 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-slate-400">
                  {batches.length === 0 ? "No FG batches yet — they appear automatically once the last operation closes for a job." : "No batches match the current filter."}
                </td></tr>
              ) : filtered.map((b, i) => (
                <tr key={b.fg_batch_no} className={`border-t border-slate-100 ${i % 2 === 0 ? "" : "bg-slate-50/40"}`}>
                  <td className="px-3 py-3 font-mono text-xs font-bold text-[#1A3560] max-w-[260px] break-all">{b.fg_batch_no}</td>
                  <td className="px-3 py-3">
                    <p className="font-bold text-slate-900 text-xs max-w-[200px] truncate" title={b.part_name}>{b.part_name}</p>
                    <p className="text-[10px] text-slate-500 font-mono">{b.part_no}</p>
                  </td>
                  <td className="px-3 py-3 text-xs">
                    <p className="font-mono text-slate-700">{b.rm_batch_code}</p>
                    <p className="text-[10px] text-slate-400">First Op {b.first_op_loading} · {b.first_op_close_date}</p>
                  </td>
                  <td className="px-3 py-3 font-bold text-[#15803D]">{b.qty}</td>
                  <td className="px-3 py-3">
                    <span className={`text-[10px] px-2 py-1 rounded-full font-bold ${b.status === "DISPATCHED" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {b.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs font-mono text-slate-700">{b.invoice_no || "—"}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <a
                        data-testid={`qr-fg-${b.fg_batch_no}`}
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/fg-batches/${encodeURIComponent(b.fg_batch_no)}/label.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all"
                        title="Print QR label"
                      >
                        <QrCode size={11} /> QR
                      </a>
                      <button
                        data-testid={`trace-fg-${b.fg_batch_no}`}
                        onClick={() => onTrace(b.fg_batch_no)}
                        className="inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all"
                      >
                        <GitBranch size={11} /> Trace
                      </button>
                      {b.status === "READY" ? (
                        <button
                          data-testid={`link-invoice-${b.fg_batch_no}`}
                          onClick={() => {
                            const inv = window.prompt(`Enter invoice number for ${b.fg_batch_no}:`);
                            if (inv && inv.trim()) onLinkInvoice(b.fg_batch_no, inv.trim());
                          }}
                          className="inline-flex items-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all"
                        >
                          Link Invoice
                        </button>
                      ) : (
                        <button
                          data-testid={`unlink-invoice-${b.fg_batch_no}`}
                          onClick={() => onUnlinkInvoice(b.fg_batch_no)}
                          className="inline-flex items-center gap-1 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all"
                        >
                          Unlink
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Reset Test Data Tab — Clear every transactional collection while keeping
// master data (machines/tools/parts/users/plans/roles) untouched.
// ============================================================================

const ResetTestDataTab = ({ showToast }) => {
  const [preview, setPreview] = useState(null);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [alsoResetPm, setAlsoResetPm] = useState(true);

  const loadPreview = () => {
    api.get("/admin/test-data/preview")
      .then((r) => setPreview(r.data))
      .catch((e) => showToast(e.response?.data?.detail || "Failed to load preview", "error"));
  };

  useEffect(() => { loadPreview(); }, []);

  const apply = async () => {
    if (phrase !== "RESET TEST DATA") {
      showToast('Type "RESET TEST DATA" exactly to confirm', "error");
      return;
    }
    if (!window.confirm("This will PERMANENTLY delete all production, inspection, downtime, and mount records. Master data (machines, tools, parts, users) stays. Continue?")) return;
    setBusy(true);
    try {
      const res = await api.post("/admin/test-data/reset", {
        confirm_phrase: phrase,
        also_reset_tool_pm: alsoResetPm,
      });
      setResult(res.data);
      setPhrase("");
      showToast(`Wiped ${res.data.deleted_total} docs across ${res.data.collections.length} collections.`);
      loadPreview();
    } catch (e) {
      showToast(e.response?.data?.detail || "Reset failed", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="reset-test-data-tab">
      <div>
        <h3 className="font-extrabold text-slate-900 inline-flex items-center gap-2">
          <Trash2 size={16} className="text-red-600" /> Reset Test / Trial Data
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Deletes every transactional record — production runs, inspections, downtime, tool mounts, RM movements, notifications, audit log — while <b>preserving master data</b> (machines, tools, parts, users, inspection plans, custom roles, permissions, IoT devices).
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800">
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-extrabold">This is destructive and cannot be undone.</p>
            <p>Only run this once the pilot / trial is over. All production dashboards and reports will show empty until real data starts flowing again.</p>
          </div>
        </div>
      </div>

      {preview && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500 mb-2">Will be deleted ({preview.total_docs} docs)</p>
            <div className="max-h-64 overflow-y-auto pr-2 text-xs space-y-1">
              {Object.entries(preview.counts).map(([name, cnt]) => (
                <div key={name} className="flex justify-between border-b border-slate-100 py-1">
                  <span className="font-mono text-slate-600">{name}</span>
                  <span className={`font-extrabold ${cnt > 0 ? "text-red-600" : "text-slate-300"}`}>{cnt}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-emerald-200 rounded-xl p-4">
            <p className="text-xs font-extrabold uppercase tracking-widest text-emerald-700 mb-2">Kept (untouched)</p>
            <div className="flex flex-wrap gap-2">
              {preview.protected_collections.map((c) => (
                <span key={c} className="text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-1 rounded-full">
                  {c}
                </span>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">Your machine list, tool master, parts, users, inspection plans, custom roles and IoT device onboarding all stay exactly as they are.</p>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            data-testid="reset-also-pm"
            checked={alsoResetPm}
            onChange={(e) => setAlsoResetPm(e.target.checked)}
            className="w-4 h-4 accent-[#1A3560]"
          />
          Also clear <b>Tool PM / Breakdown history</b> (uncheck if you want to keep it for trend analysis)
        </label>
        <label className="block text-xs font-bold text-slate-700">
          Type <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">RESET TEST DATA</span> to enable the button
          <input
            type="text"
            data-testid="reset-confirm-input"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder="RESET TEST DATA"
            className="mt-1 w-full border border-slate-300 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 font-mono"
          />
        </label>
        <div className="flex justify-end">
          <button
            onClick={apply}
            data-testid="reset-apply-btn"
            disabled={busy || phrase !== "RESET TEST DATA"}
            className="text-sm font-extrabold text-white bg-red-600 hover:bg-red-700 disabled:bg-slate-300 px-4 py-2 rounded-lg inline-flex items-center gap-2 shadow"
          >
            <Trash2 size={14} /> {busy ? "Wiping…" : "Reset Transactional Data"}
          </button>
        </div>
      </div>

      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-xs">
          <p className="font-extrabold text-emerald-800 mb-1 inline-flex items-center gap-2">
            <CheckCircle2 size={14} /> Reset complete — {result.deleted_total} docs removed
          </p>
          <div className="mt-2 grid grid-cols-2 gap-1">
            {Object.entries(result.per_collection).map(([name, n]) => (
              <div key={name} className="font-mono text-slate-600">
                {name}: <span className="font-extrabold">{typeof n === "number" ? n : "ERROR"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;

// ============================================================================
// Bulk Tools Tab — landing card that links to the full Bulk Tool Setup page.
// ============================================================================
const BulkToolsTab = ({ navigate }) => (
  <div className="space-y-4" data-testid="bulk-tools-tab">
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div>
        <h3 className="font-extrabold text-slate-900 inline-flex items-center gap-2">
          <Upload size={16} /> Bulk Tool Setup
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Import or update the Tool Master in bulk via CSV. Ideal for onboarding a new machine or refreshing PM ranges across many tools at once.
        </p>
      </div>
      <button
        data-testid="open-bulk-tool-setup"
        onClick={() => navigate("/admin/bulk-tool-setup")}
        className="text-xs font-extrabold text-white bg-[#1A3560] hover:bg-[#152a4c] px-4 py-2 rounded-lg inline-flex items-center gap-2 shadow"
      >
        <Upload size={12} /> Open Bulk Tool Setup
      </button>
    </div>
    <div className="bg-white border border-slate-200 rounded-xl p-4 grid md:grid-cols-3 gap-4 text-xs">
      <div>
        <p className="font-extrabold text-slate-800 mb-1">1. Download template</p>
        <p className="text-slate-500">Get a CSV pre-populated with the current Tool Master so you can edit inline.</p>
      </div>
      <div>
        <p className="font-extrabold text-slate-800 mb-1">2. Edit &amp; upload</p>
        <p className="text-slate-500">Change tool life, PM ranges, part linkage, active flag — save and upload back.</p>
      </div>
      <div>
        <p className="font-extrabold text-slate-800 mb-1">3. Review the diff</p>
        <p className="text-slate-500">Every import writes to the CSV Import Log so admins can audit every change.</p>
      </div>
    </div>
  </div>
);

// ============================================================================
// Role Access Tab — checkbox matrix that toggles per-role visibility of the
// features listed in `/api/admin/role-permissions/catalog`.
// ============================================================================

const RoleAccessTab = () => {
  const { rolesChangedAt } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [matrix, setMatrix] = useState({}); // key `${role}::${featureKey}` -> bool
  const [initial, setInitial] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [filter, setFilter] = useState("");

  // Custom-role management state
  const [customRoles, setCustomRoles] = useState([]);
  const [newRoleName, setNewRoleName] = useState("");
  const [roleErr, setRoleErr] = useState("");
  const [rolesBusy, setRolesBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/role-permissions"),
      api.get("/admin/custom-roles"),
    ])
      .then(([mRes, cRes]) => {
        const m = {};
        (mRes.data.matrix || []).forEach((c) => { m[`${c.role}::${c.feature_key}`] = c.enabled; });
        setCatalog({ roles: mRes.data.roles, features: mRes.data.features });
        setMatrix(m);
        setInitial({ ...m });
        setCustomRoles(cRes.data || []);
      })
      .catch((e) => setError(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);
  // Live-refresh whenever another admin adds/removes a role via WebSocket.
  useEffect(() => { if (rolesChangedAt) load(); }, [rolesChangedAt]);

  const setCell = (role, fk, enabled) => {
    setMatrix((prev) => ({ ...prev, [`${role}::${fk}`]: enabled }));
  };

  const dirty = Object.keys(matrix).filter((k) => matrix[k] !== initial[k]);

  const save = async () => {
    if (dirty.length === 0) return;
    setSaving(true);
    setError("");
    try {
      const updates = dirty.map((k) => {
        const [role, feature_key] = k.split("::");
        return { role, feature_key, enabled: !!matrix[k] };
      });
      await api.put("/admin/role-permissions", { updates });
      setInitial({ ...matrix });
      setToast(`Saved ${updates.length} change${updates.length !== 1 ? "s" : ""}. Users see updates on next login.`);
      setTimeout(() => setToast(""), 4000);
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setMatrix({ ...initial });

  const setAllForRole = (role, enabled) => {
    setMatrix((prev) => {
      const next = { ...prev };
      (catalog?.features || []).forEach((f) => {
        next[`${role}::${f.key}`] = enabled;
      });
      return next;
    });
  };

  const addRole = async () => {
    const name = (newRoleName || "").trim();
    if (!name) return;
    setRolesBusy(true);
    setRoleErr("");
    try {
      await api.post("/admin/custom-roles", { role_name: name });
      setNewRoleName("");
      load();
    } catch (e) {
      setRoleErr(e.response?.data?.detail || "Failed to add role");
    } finally {
      setRolesBusy(false);
    }
  };

  const toggleRoleActive = async (role) => {
    setRolesBusy(true);
    setRoleErr("");
    try {
      await api.put(`/admin/custom-roles/${encodeURIComponent(role.role_name)}`,
        { active: !role.active });
      load();
    } catch (e) {
      setRoleErr(e.response?.data?.detail || "Failed to update role");
    } finally {
      setRolesBusy(false);
    }
  };

  const deleteRole = async (role) => {
    if (!window.confirm(`Delete role "${role.role_name}"? Only allowed if no users are assigned.`)) return;
    setRolesBusy(true);
    setRoleErr("");
    try {
      await api.delete(`/admin/custom-roles/${encodeURIComponent(role.role_name)}`);
      load();
    } catch (e) {
      setRoleErr(e.response?.data?.detail || "Failed to delete role");
    } finally {
      setRolesBusy(false);
    }
  };

  if (loading || !catalog) {
    return <div className="text-center text-slate-400 py-10 text-sm">Loading permissions…</div>;
  }

  const grouped = {};
  (catalog.features || []).forEach((f) => {
    const label = (f.label || "").toLowerCase();
    if (filter && !label.includes(filter.toLowerCase())) return;
    grouped[f.category] = grouped[f.category] || [];
    grouped[f.category].push(f);
  });

  return (
    <div className="space-y-4" data-testid="role-access-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-extrabold text-slate-900 inline-flex items-center gap-2">
            <ShieldCheck size={16} /> Role Access Control
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">Choose which Home-page tiles each role can see. Main Admin always sees everything.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={filter}
            data-testid="role-access-filter"
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter features…"
            className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-[#1A3560] focus:outline-none"
          />
          <button
            onClick={reset}
            disabled={dirty.length === 0}
            data-testid="role-access-reset"
            className="text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40 px-3 py-1.5 rounded-lg border border-slate-200"
          >
            Reset
          </button>
          <button
            onClick={save}
            disabled={dirty.length === 0 || saving}
            data-testid="role-access-save"
            className="text-xs font-extrabold text-white bg-[#15803D] hover:bg-emerald-700 disabled:bg-slate-300 px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
          >
            <Save size={12} /> {saving ? "Saving…" : `Save${dirty.length ? ` (${dirty.length})` : ""}`}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg">{error}</div>}
      {toast && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-3 py-2 rounded-lg inline-flex items-center gap-2"><CheckCircle2 size={12} /> {toast}</div>}

      {/* Manage custom roles */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <p className="font-extrabold text-slate-900 text-sm inline-flex items-center gap-2">
              <Users size={14} /> Custom Roles
            </p>
            <p className="text-[11px] text-slate-500">Add any additional roles you need (e.g. "Visual Inspector", "Quality Incharge"). They'll appear as new columns in the matrix below.</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              data-testid="new-role-name"
              placeholder="New role name…"
              className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-[#1A3560] focus:outline-none w-56"
              onKeyDown={(e) => e.key === "Enter" && addRole()}
            />
            <button
              onClick={addRole}
              disabled={rolesBusy || !newRoleName.trim()}
              data-testid="add-role-btn"
              className="text-xs font-extrabold text-white bg-[#1A3560] hover:bg-[#152a4c] disabled:bg-slate-300 px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
            >
              <Plus size={12} /> Add Role
            </button>
          </div>
        </div>
        {roleErr && <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg mb-2">{roleErr}</div>}
        {customRoles.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No custom roles yet. Built-in roles (Supervisor, Inspector, Operator, Stores) are always shown.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {customRoles.map((r) => (
              <div
                key={r.role_name}
                data-testid={`custom-role-${r.role_name}`}
                className={`inline-flex items-center gap-2 border rounded-full px-3 py-1 text-xs ${r.active ? "bg-slate-50 border-slate-200" : "bg-slate-100 border-slate-200 text-slate-400"}`}
              >
                <span className={`font-extrabold ${r.active ? "text-slate-800" : "text-slate-400 line-through"}`}>{r.role_name}</span>
                <span className="text-[10px] text-slate-400">{r.user_count} user{r.user_count === 1 ? "" : "s"}</span>
                <button
                  onClick={() => toggleRoleActive(r)}
                  data-testid={`toggle-role-${r.role_name}`}
                  className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${r.active ? "text-amber-700 hover:bg-amber-100" : "text-emerald-700 hover:bg-emerald-100"}`}
                  disabled={rolesBusy}
                  title={r.active ? "Deactivate (hides in dropdowns and matrix)" : "Reactivate"}
                >
                  {r.active ? "Deactivate" : "Reactivate"}
                </button>
                <button
                  onClick={() => deleteRole(r)}
                  data-testid={`delete-role-${r.role_name}`}
                  className="text-[10px] font-bold rounded-full px-2 py-0.5 text-red-600 hover:bg-red-100 disabled:opacity-40"
                  disabled={rolesBusy || r.user_count > 0}
                  title={r.user_count > 0 ? "Reassign or deactivate users first" : "Delete role"}
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase font-extrabold text-slate-500 tracking-wider sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left">Feature</th>
              {catalog.roles.map((r) => (
                <th key={r} className="px-4 py-3 text-center">
                  <div>{r}</div>
                  <div className="mt-1 flex justify-center gap-1 text-[9px] font-bold normal-case">
                    <button
                      onClick={() => setAllForRole(r, true)}
                      className="text-emerald-700 hover:underline"
                      data-testid={`role-all-on-${r}`}
                    >
                      all on
                    </button>
                    <span className="text-slate-300">·</span>
                    <button
                      onClick={() => setAllForRole(r, false)}
                      className="text-red-700 hover:underline"
                      data-testid={`role-all-off-${r}`}
                    >
                      all off
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {Object.entries(grouped).map(([cat, feats]) => (
              <Fragment key={`grp-${cat}`}>
                <tr className="bg-slate-50">
                  <td colSpan={catalog.roles.length + 1} className="px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-widest text-slate-400">
                    {cat}
                  </td>
                </tr>
                {feats.map((f) => (
                  <tr key={f.key} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <div className="font-bold text-slate-900 text-sm">{f.label}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{f.key}</div>
                    </td>
                    {catalog.roles.map((role) => {
                      const key = `${role}::${f.key}`;
                      const isDirty = matrix[key] !== initial[key];
                      return (
                        <td key={key} className="px-4 py-2 text-center">
                          <label className="inline-flex items-center justify-center relative">
                            <input
                              type="checkbox"
                              data-testid={`perm-${role}-${f.key}`}
                              checked={!!matrix[key]}
                              onChange={(e) => setCell(role, f.key, e.target.checked)}
                              className="w-4 h-4 accent-[#1A3560]"
                            />
                            {isDirty && (
                              <span className="absolute -top-1 -right-2 w-1.5 h-1.5 bg-amber-500 rounded-full" title="Unsaved change" />
                            )}
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-slate-400">
        <AlertTriangle size={11} className="inline mr-1" />
        Changes take effect for each user on their next login or page refresh. Main Admin is not shown here — admins always see every feature.
      </p>
    </div>
  );
};


// ---------- Root Causes Tab ----------
const RC_PALETTE = ["#DC2626", "#D97706", "#2563EB", "#059669", "#7C3AED", "#DB2777", "#0891B2", "#CA8A04"];

const RootCausesTab = ({ showToast }) => {
  const [data, setData] = useState({ root_causes: [], unassigned_reason_codes: [], all_reason_codes: [] });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { editing: {} | null }

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/downtime/root-causes");
      setData(res.data);
    } catch (e) {
      showToast(e.response?.data?.detail || "Failed to load root causes", "error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openCreate = () => setModal({ editing: null });
  const openEdit = (rc) => setModal({ editing: rc });

  const remove = async (rc) => {
    if (!window.confirm(`Delete root cause "${rc.name}"? Reason codes under it will become unassigned.`)) return;
    try {
      await api.delete(`/downtime/root-causes/${rc.id}`);
      showToast("Root cause deleted");
      load();
    } catch (e) {
      showToast(e.response?.data?.detail || "Failed to delete", "error");
    }
  };

  return (
    <div data-testid="root-causes-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="font-extrabold text-slate-900 inline-flex items-center gap-2">
            <Target size={18} className="text-[#1A3560]" /> Downtime Root Causes
          </h2>
          <p className="text-xs text-slate-400">
            Cluster repeat downtime reasons under a single named bucket so the weekly report highlights real patterns.
          </p>
        </div>
        <button
          data-testid="add-root-cause-btn"
          onClick={openCreate}
          className="flex items-center gap-2 bg-[#1A3560] text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-[#102340] active:scale-[0.98] transition-all"
        >
          <Plus size={16} /> Add Root Cause
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-400 text-sm">Loading…</div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2" data-testid="root-causes-list">
            {data.root_causes.length === 0 && (
              <div className="col-span-full bg-white border border-slate-200 rounded-xl p-6 text-center text-sm text-slate-500">
                No root causes defined yet. Click <b>Add Root Cause</b> to start grouping reason codes.
              </div>
            )}
            {data.root_causes.map((rc) => (
              <div
                key={rc.id}
                data-testid={`root-cause-card-${rc.id}`}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-4"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="h-4 w-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: rc.color || "#64748B" }}
                    />
                    <p className="font-extrabold text-slate-900 truncate">{rc.name}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      data-testid={`edit-root-cause-${rc.id}`}
                      onClick={() => openEdit(rc)}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      data-testid={`delete-root-cause-${rc.id}`}
                      onClick={() => remove(rc)}
                      className="p-1.5 rounded hover:bg-red-50 text-red-600"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                  {rc.reason_codes.length} reason code(s)
                </p>
                <div className="flex flex-wrap gap-1">
                  {rc.reason_codes.length === 0 ? (
                    <span className="text-xs italic text-slate-400">No reason codes assigned</span>
                  ) : rc.reason_codes.map((code) => {
                    const label = (data.all_reason_codes.find((c) => c.code === code) || {}).label || code;
                    return (
                      <span key={code} className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                        {label}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="unassigned-codes-panel">
            <p className="text-xs font-extrabold uppercase tracking-widest text-amber-800 mb-2">
              Unassigned reason codes ({data.unassigned_reason_codes.length})
            </p>
            {data.unassigned_reason_codes.length === 0 ? (
              <p className="text-xs text-amber-700 italic">All reason codes are assigned to a root cause.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {data.unassigned_reason_codes.map((code) => {
                  const label = (data.all_reason_codes.find((c) => c.code === code) || {}).label || code;
                  return (
                    <span key={code} className="text-[11px] bg-white border border-amber-300 text-amber-900 px-2 py-0.5 rounded">
                      {label}
                    </span>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-amber-700 mt-2">
              These will appear in a synthetic &quot;Ungrouped&quot; bucket on the weekly report chart.
            </p>
          </div>
        </>
      )}

      {modal && (
        <RootCauseEditor
          editing={modal.editing}
          allReasonCodes={data.all_reason_codes}
          existing={data.root_causes}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
          showToast={showToast}
        />
      )}
    </div>
  );
};

const RootCauseEditor = ({ editing, allReasonCodes, existing, onClose, onSaved, showToast }) => {
  const defaultColor = editing?.color || RC_PALETTE[(existing.length || 0) % RC_PALETTE.length];
  const [name, setName] = useState(editing?.name || "");
  const [color, setColor] = useState(defaultColor);
  const [codes, setCodes] = useState(editing?.reason_codes || []);
  const [saving, setSaving] = useState(false);

  const owner = {};
  existing.forEach((rc) => {
    if (editing && rc.id === editing.id) return;
    (rc.reason_codes || []).forEach((c) => { owner[c] = rc; });
  });

  const toggle = (code) =>
    setCodes((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  const submit = async () => {
    if (!name.trim()) {
      showToast("Name is required", "error");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/downtime/root-causes/${editing.id}`, { name: name.trim(), color, reason_codes: codes });
        showToast("Root cause updated");
      } else {
        await api.post("/downtime/root-causes", { name: name.trim(), color, reason_codes: codes });
        showToast("Root cause created");
      }
      onSaved();
    } catch (e) {
      showToast(e.response?.data?.detail || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="root-cause-editor">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <p className="font-extrabold text-slate-900">{editing ? "Edit Root Cause" : "New Root Cause"}</p>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-500">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Name <span className="text-red-500">*</span></label>
            <input
              data-testid="rc-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Material supply chain"
              className="w-full border-slate-300 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-[#1A3560]"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1 inline-flex items-center gap-1.5">
              <Palette size={12} /> Color
            </label>
            <div className="flex gap-1.5 flex-wrap">
              {RC_PALETTE.map((c) => (
                <button
                  key={c}
                  data-testid={`rc-color-${c}`}
                  onClick={() => setColor(c)}
                  style={{ backgroundColor: c }}
                  className={`h-7 w-7 rounded-full border-2 transition-transform ${color === c ? "border-slate-900 scale-110" : "border-white"}`}
                  title={c}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">
              Reason codes in this group
            </label>
            <div className="border border-slate-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-slate-100" data-testid="rc-code-list">
              {allReasonCodes.map((rc) => {
                const checked = codes.includes(rc.code);
                const stolen = owner[rc.code];
                return (
                  <label
                    key={rc.code}
                    data-testid={`rc-code-row-${rc.code}`}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(rc.code)}
                      className="h-3.5 w-3.5 accent-[#1A3560]"
                    />
                    <span className="font-bold text-slate-800 flex-1">{rc.label}</span>
                    {stolen && checked && (
                      <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                        was in &quot;{stolen.name}&quot;
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              A reason code can belong to only one group. Ticking it here will move it out of its current group.
            </p>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-sm font-bold text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 px-4 py-2 rounded-lg">
            Cancel
          </button>
          <button
            data-testid="rc-save-btn"
            onClick={submit}
            disabled={saving}
            className="text-sm font-extrabold text-white bg-[#1A3560] hover:bg-[#102340] disabled:bg-slate-300 px-4 py-2 rounded-lg inline-flex items-center gap-1.5"
          >
            <Save size={14} /> {saving ? "Saving…" : (editing ? "Save Changes" : "Create")}
          </button>
        </div>
      </div>
    </div>
  );
};
